import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { Water } from 'three/addons/objects/Water.js';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { ALLEGIANCES, CONTRACT_OFFERS, CREW, SHIPS, UPGRADES, VERDANT_LIGHTS } from './data';
import { ProceduralAudio } from './ProceduralAudio';
import { applyDamage, broadsideSideSign, isOutsideCircle, relativeWindFactor, sampleWaveHeight, seededRandom, shortestAngleDelta } from './simulation';
import type {
  ContractState,
  AllegianceId,
  CameraView,
  ContractOfferId,
  CrewId,
  FloatingTextState,
  GameMode,
  GameSettings,
  ProjectileState,
  ShipDefinition,
  ShipState,
  Side,
  UpgradeDefinition,
} from './types';

declare global {
  interface Window {
    render_game_to_text: () => string;
    advanceTime: (ms: number) => void;
  }
}

type EffectState = {
  object: THREE.Mesh;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  kind: 'smoke' | 'splash' | 'wake' | 'magic' | 'spray';
};

type WakeSurface = {
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  localXZ: Float32Array;
  strengthScale: number;
};

type WakeRig = {
  ship: ShipState;
  surfaces: WakeSurface[];
  lastHeading: number;
};

type ClothUniforms = {
  time: { value: number };
  wind: { value: THREE.Vector3 };
  strength: { value: number };
  flutter: { value: number };
};

type ClothRig = {
  ship: ShipState;
  uniforms: ClothUniforms;
};

type HudElements = {
  root: HTMLElement;
  objective: HTMLElement;
  distance: HTMLElement;
  hull: HTMLElement;
  hullText: HTMLElement;
  sails: HTMLElement;
  sailsText: HTMLElement;
  port: HTMLElement;
  starboard: HTMLElement;
  blink: HTMLElement;
  cargo: HTMLElement;
  gold: HTMLElement;
  windArrow: HTMLElement;
  speed: HTMLElement;
  trim: HTMLElement;
  context: HTMLElement;
  contextText: HTMLElement;
  cameraMode: HTMLElement;
  deckReticle: HTMLElement;
  enemy: HTMLElement;
  enemyHull: HTMLElement;
  damageFlash: HTMLElement;
  blinkFlash: HTMLElement;
};

const UP = new THREE.Vector3(0, 1, 0);
const HARBOR_CENTER = new THREE.Vector3(0, 0, -40);
const HARBOR_RADIUS_X = 178;
const HARBOR_RADIUS_Z = 158;
const DOCK_POSITION = new THREE.Vector3(18, 0, 38);
const PLAYER_SPAWN = new THREE.Vector3(-5, 0, 48);
const EVENT_POSITION = new THREE.Vector3(26, 0, 210);
const PIRATE_POSITION = new THREE.Vector3(-8, 0, 374);
const FIXED_STEP = 1 / 60;
const indexToWakeColor = (phase: number): number => phase === 2 ? 0xa6d7d0 : 0xd8f1ec;

export class BlackwakeGame {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(52, 1, 0.1, 2400);
  private readonly renderer: THREE.WebGLRenderer;
  private readonly loader = new GLTFLoader();
  private readonly settings: GameSettings = {
    masterVolume: 0.72,
    musicVolume: 0.6,
    effectsVolume: 0.85,
    cameraShake: true,
  };
  private readonly audio = new ProceduralAudio(this.settings);
  private readonly keys = new Set<string>();
  private readonly projectiles: ProjectileState[] = [];
  private readonly effects: EffectState[] = [];
  private readonly floatingTexts: FloatingTextState[] = [];
  private readonly upgrades = new Set<UpgradeDefinition['id']>();
  private readonly wind = new THREE.Vector2(0.45, 0.89).normalize();
  private readonly wakeRigs = new Map<ShipState['kind'], WakeRig>();
  private readonly clothRigs = new Map<ShipState['kind'], ClothRig>();

  private mode: GameMode = 'loading';
  private priorMode: GameMode = 'sailing';
  private player!: ShipState;
  private pirate!: ShipState;
  private pirateVisible = false;
  private pirateSinking = 0;
  private pirateAiSide = 1;
  private eventGroup = new THREE.Group();
  private eventRing!: THREE.Mesh;
  private lootGroup = new THREE.Group();
  private lootRing!: THREE.Mesh;
  private objectiveBeacon = new THREE.Group();
  private ocean!: Water;
  private harborGroup = new THREE.Group();
  private hud!: HudElements;
  private contract: ContractState = this.newContract();
  private gold = 0;
  private projectileId = 0;
  private elapsed = 0;
  private accumulator = 0;
  private lastFrame = 0;
  private cameraYaw = 0;
  private cameraPitch = 0.2;
  private cameraDistance = 52;
  private battleCameraEnabled = true;
  private cameraView: CameraView = 'captain';
  private readonly deckPosition = new THREE.Vector3(-2.2, 6.35, -6);
  private deckYaw = 0;
  private deckPitch = -0.03;
  private cameraShake = 0;
  private lastBroadside: { owner: ShipState['kind']; side: Side; x: number; z: number } | null = null;
  private draggingCamera = false;
  private lastPointer = new THREE.Vector2();
  private wakeTimer = 0;
  private allegiance: AllegianceId | null = null;
  private crew: CrewId | null = null;
  private selectedContract: ContractOfferId = 'wraith-hunt';
  private acceptedContract: ContractOfferId | null = null;
  private nearbyAction: 'none' | 'dock' | 'loot' = 'none';
  private rngState: number;
  private testMode: boolean;
  private testScenario: string;
  private forcedEventOutcome: 'relic' | 'ambush' | null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly hudRoot: HTMLElement,
    private readonly modalRoot: HTMLElement,
    private readonly toastRoot: HTMLElement,
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.loader.setMeshoptDecoder(MeshoptDecoder);

    const params = new URLSearchParams(window.location.search);
    this.testMode = params.has('test');
    this.testScenario = params.get('scenario') ?? '';
    this.rngState = Number(params.get('seed') ?? 73421) || 73421;
    const forced = params.get('event');
    this.forcedEventOutcome = forced === 'relic' || forced === 'ambush' ? forced : null;
  }

  async start(): Promise<void> {
    this.buildScene();
    this.buildHud();
    this.bindInputs();
    this.installTestHooks();
    this.resize();

    const label = document.querySelector<HTMLElement>('#loading-label');
    const bar = document.querySelector<HTMLElement>('#loading-bar');
    this.setLoading(12, 'Raising the harbor…', bar, label);

    this.setLoading(34, 'Rigging the Moonglass…', bar, label);
    const harborPromise = this.loadHarborModels();
    const [player, pirate] = await Promise.all([
      this.createShip(SHIPS.cutter, PLAYER_SPAWN, 0),
      this.createShip(SHIPS.pirate, PIRATE_POSITION, Math.PI),
      harborPromise,
    ]).then(([loadedPlayer, loadedPirate]) => [loadedPlayer, loadedPirate]);
    this.player = player;
    this.pirate = pirate;
    this.pirate.object.visible = false;
    this.scene.add(this.pirate.object);
    this.setLoading(86, 'Sounding Lantern Shoal…', bar, label);
    this.setLoading(100, 'The tide is ready.', bar, label);
    this.scene.add(this.player.object);
    this.createWakeRig(this.player);
    this.createWakeRig(this.pirate);

    this.mode = 'title';
    this.updateHud();
    if (this.testMode && this.testScenario) this.applyTestScenario(this.testScenario);
    else this.showTitle();
    this.snapCamera();
    document.body.dataset.gameReady = 'true';
    if (this.testMode) {
      const ready = document.createElement('button');
      ready.id = 'test-ready';
      ready.type = 'button';
      ready.setAttribute('aria-label', 'Game ready');
      ready.style.cssText = 'position:fixed;right:0;top:0;width:2px;height:2px;opacity:.01;z-index:200;pointer-events:auto;padding:0;border:0';
      document.body.appendChild(ready);
    }
    const loadingScreen = document.querySelector('#loading-screen');
    if (this.testMode) loadingScreen?.classList.add('hidden');
    else window.setTimeout(() => loadingScreen?.classList.add('hidden'), 240);

    this.lastFrame = performance.now();
    requestAnimationFrame(this.loop);
  }

  private buildScene(): void {
    this.scene.background = new THREE.Color(0x668b9b);
    this.scene.fog = new THREE.FogExp2(0x7897a0, 0.00078);

    const hemisphere = new THREE.HemisphereLight(0xd8edf0, 0x26372d, 1.75);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight(0xffd9a1, 4.8);
    sun.position.set(-210, 180, 115);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -180;
    sun.shadow.camera.right = 180;
    sun.shadow.camera.top = 180;
    sun.shadow.camera.bottom = -180;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 600;
    sun.shadow.bias = -0.0002;
    this.scene.add(sun);

    const harborFill = new THREE.DirectionalLight(0xbfd7d5, 0.72);
    harborFill.position.set(190, 120, 170);
    harborFill.target.position.set(0, 18, -105);
    this.scene.add(harborFill, harborFill.target);

    const skyGeometry = new THREE.SphereGeometry(1100, 32, 18);
    const skyMaterial = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor: { value: new THREE.Color(0x315f79) },
        horizonColor: { value: new THREE.Color(0xc6d3cf) },
        bottomColor: { value: new THREE.Color(0x315c6b) },
      },
      vertexShader: `varying vec3 vPosition; void main(){ vPosition = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
      fragmentShader: `
        varying vec3 vPosition;
        uniform vec3 topColor;
        uniform vec3 horizonColor;
        uniform vec3 bottomColor;
        void main(){
          float h = normalize(vPosition).y;
          vec3 color = h > 0.0 ? mix(horizonColor, topColor, pow(h,0.55)) : mix(horizonColor,bottomColor,clamp(-h*3.0,0.0,1.0));
          gl_FragColor = vec4(color,1.0);
        }
      `,
    });
    this.scene.add(new THREE.Mesh(skyGeometry, skyMaterial));

    const waterNormals = new THREE.TextureLoader().load('/assets/textures/waternormals.jpg');
    waterNormals.wrapS = waterNormals.wrapT = THREE.RepeatWrapping;
    const oceanGeometry = new THREE.PlaneGeometry(2400, 2400);
    this.ocean = new Water(oceanGeometry, {
      textureWidth: 512,
      textureHeight: 512,
      waterNormals,
      sunDirection: new THREE.Vector3(-0.48, 0.78, -0.4).normalize(),
      sunColor: 0xc9e1df,
      waterColor: 0x003344,
      distortionScale: 3.7,
      fog: true,
    });
    (this.ocean.material as THREE.ShaderMaterial).uniforms.size.value = 1.35;
    this.ocean.rotation.x = -Math.PI / 2;
    // Large sail silhouettes read as black oil slicks on the reflective plane;
    // foam and spray carry the boat/water contact instead.
    this.ocean.receiveShadow = false;
    this.scene.add(this.ocean);

    this.buildHarborTerrain();
    this.buildEventMarker();
    this.buildLootMarker();
    this.buildObjectiveBeacon();
    this.buildClouds();
  }

  private buildHarborTerrain(): void {
    this.harborGroup.position.copy(HARBOR_CENTER);
    this.scene.add(this.harborGroup);

    // The harbor is one physical island. The cinematic menu camera and sailing
    // camera both look at this same mesh, so every pier, roof, and road remains
    // spatially consistent when the player casts off.
    const coastGeometry = new THREE.PlaneGeometry(404, 350, 82, 72);
    coastGeometry.rotateX(-Math.PI / 2);
    const positions = coastGeometry.attributes.position as THREE.BufferAttribute;
    const colors = new Float32Array(positions.count * 3);
    const rock = new THREE.Color(0x56655f);
    const moss = new THREE.Color(0x365440);
    const earth = new THREE.Color(0x625e4f);
    for (let i = 0; i < positions.count; i += 1) {
      const x = positions.getX(i);
      const z = positions.getZ(i) - 82;
      const y = this.harborTerrainHeight(x, z);
      positions.setXYZ(i, x, y, z);
      const grassNoise = Math.sin(x * 0.057 + z * 0.022) * 0.28 + Math.cos(x * 0.031 - z * 0.064) * 0.24;
      const grassAmount = THREE.MathUtils.clamp(0.48 + grassNoise - Math.max(0, y - 25) * 0.025, 0.05, 0.92);
      const tint = rock.clone().lerp(moss, grassAmount);
      if (y < 2.2) tint.lerp(earth, THREE.MathUtils.clamp((2.2 - y) / 2.4, 0, 1));
      colors[i * 3] = tint.r;
      colors[i * 3 + 1] = tint.g;
      colors[i * 3 + 2] = tint.b;
    }
    coastGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    coastGeometry.computeVertexNormals();
    const coast = new THREE.Mesh(coastGeometry, new THREE.MeshStandardMaterial({
      color: 0xffffff,
      map: this.createTerrainDetailTexture(),
      vertexColors: true,
      roughness: 1,
      flatShading: true,
    }));
    coast.receiveShadow = true;
    coast.castShadow = true;
    this.harborGroup.add(coast);
    this.buildCoastalCliffs();

    const coastalRockGeometry = new THREE.DodecahedronGeometry(1, 0);
    const coastalRockMaterials = [0x46534e, 0x52605a, 0x3b4944].map((color) => new THREE.MeshStandardMaterial({ color, roughness: 1, flatShading: true }));
    for (let i = 0; i < 76; i += 1) {
      const angle = i / 76 * Math.PI * 2;
      const x = Math.cos(angle) * (HARBOR_RADIUS_X + Math.sin(i * 4.71) * 7);
      const z = -82 + Math.sin(angle) * (HARBOR_RADIUS_Z + Math.cos(i * 3.13) * 6);
      if (z > 35 && Math.abs(x) < 112) continue;
      const boulder = new THREE.Mesh(coastalRockGeometry, coastalRockMaterials[i % coastalRockMaterials.length]);
      boulder.position.set(x, 0.6 + (i % 3) * 0.55, z);
      boulder.scale.set(2.6 + (i % 5) * 0.62, 1.9 + (i % 4) * 0.52, 2.5 + ((i + 2) % 5) * 0.58);
      boulder.rotation.set(i * 0.31, i * 0.77, i * 0.19);
      boulder.castShadow = true;
      boulder.receiveShadow = true;
      this.harborGroup.add(boulder);
    }

    for (let i = 0; i < 64; i += 1) {
      const angle = i * 2.399;
      const radius = 48 + (i % 8) * 12;
      const x = 28 + Math.sin(angle) * radius;
      const z = -154 + Math.cos(angle) * radius * 0.66;
      if (z > -72 && Math.abs(x) < 95) continue;
      const boulder = new THREE.Mesh(coastalRockGeometry, coastalRockMaterials[(i + 1) % coastalRockMaterials.length]);
      boulder.position.set(x, this.harborTerrainHeight(x, z) - 0.2, z);
      boulder.scale.set(2.2 + (i % 4) * 0.66, 1.7 + (i % 5) * 0.46, 2.1 + ((i + 1) % 4) * 0.62);
      boulder.rotation.set(i * 0.23, i * 0.91, i * 0.17);
      boulder.castShadow = true;
      boulder.receiveShadow = true;
      this.harborGroup.add(boulder);
    }

    // The stonework stays inland as a sea wall. The warehouse's authored piles
    // now stand directly in the water instead of resting on this geometry.
    const stoneMaterial = new THREE.MeshStandardMaterial({ color: 0x50554f, roughness: 0.96 });
    const seaWall = new THREE.Mesh(new RoundedBoxGeometry(154, 7, 22, 4, 1.1), stoneMaterial);
    seaWall.position.set(-52, 1.1, 24);
    seaWall.castShadow = true;
    seaWall.receiveShadow = true;
    this.harborGroup.add(seaWall);

    // A working harbor: warehouse apron, two mooring fingers, and a separate
    // service pier. Each one is built from individual planks, beams, and piles.
    const harborPiers = [
      { x: 10, z: 77, width: 8, length: 72, yaw: 0 },
      { x: -20, z: 80, width: 6.5, length: 48, yaw: 0 },
      { x: 45, z: 75, width: 6.5, length: 54, yaw: 0 },
      { x: -25, z: 57, width: 7, length: 78, yaw: Math.PI / 2 },
    ];
    for (const item of harborPiers) {
      const pier = this.createHarborPier(item.width, item.length);
      pier.position.set(item.x, 2.2, item.z);
      pier.rotation.y = item.yaw;
      this.harborGroup.add(pier);
    }

    const crane = new THREE.Group();
    const craneWood = new THREE.MeshStandardMaterial({ color: 0x35251b, roughness: 0.94 });
    const cranePost = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.72, 15, 10), craneWood);
    cranePost.position.y = 8;
    const craneArm = new THREE.Mesh(new THREE.BoxGeometry(13, 0.7, 0.8), craneWood);
    craneArm.position.set(5.4, 15, 0);
    craneArm.rotation.z = -0.08;
    crane.add(cranePost, craneArm);
    crane.position.set(-22, 2.5, 57);
    crane.castShadow = true;
    this.harborGroup.add(crane);

    this.buildHarborCity();

    const dockRing = new THREE.Mesh(
      new THREE.RingGeometry(18, 20, 64),
      new THREE.MeshBasicMaterial({ color: 0xc8a95d, transparent: true, opacity: 0.18, side: THREE.DoubleSide }),
    );
    dockRing.rotation.x = -Math.PI / 2;
    dockRing.position.copy(DOCK_POSITION).add(new THREE.Vector3(0, 0.3, 0));
    this.scene.add(dockRing);
  }

  private createHarborPier(width: number, length: number): THREE.Group {
    const group = new THREE.Group();
    const plankMaterials = [0x38271d, 0x443024, 0x2e2119].map((color) => new THREE.MeshStandardMaterial({ color, roughness: 0.96 }));
    const structureMaterial = new THREE.MeshStandardMaterial({ color: 0x271b15, roughness: 1 });
    const plankStep = 1.28;
    const plankCount = Math.floor(length / plankStep);
    for (let i = 0; i < plankCount; i += 1) {
      const plank = new THREE.Mesh(
        new THREE.BoxGeometry(width - 0.12 - (i % 4 === 0 ? 0.12 : 0), 0.26, plankStep * 0.88),
        plankMaterials[i % plankMaterials.length],
      );
      plank.position.set((i % 7 === 0 ? 0.05 : 0), 0, -length * 0.5 + plankStep * (i + 0.5));
      plank.rotation.y = Math.sin(i * 7.13) * 0.003;
      plank.castShadow = true;
      plank.receiveShadow = true;
      group.add(plank);
    }
    for (const x of [-width * 0.34, width * 0.34]) {
      const beam = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.52, length), structureMaterial);
      beam.position.set(x, -0.48, 0);
      beam.castShadow = true;
      group.add(beam);
      for (let z = -length * 0.5 + 3.5; z < length * 0.5; z += 9) {
        const pile = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.52, 8.5, 9), structureMaterial);
        pile.position.set(x, -3.4, z);
        pile.rotation.z = Math.sin(z * 0.2 + x) * 0.018;
        pile.castShadow = true;
        group.add(pile);
      }
    }
    return group;
  }

  private buildCoastalCliffs(): void {
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const segments = 112;
    const cliffColors = [new THREE.Color(0x3d4946), new THREE.Color(0x59635c), new THREE.Color(0x47534f)];
    for (let i = 0; i < segments; i += 1) {
      const a0 = i / segments * Math.PI * 2;
      const a1 = (i + 1) / segments * Math.PI * 2;
      const mid = (a0 + a1) * 0.5;
      const harborMouth = Math.sin(mid) > 0.57 && Math.abs(Math.cos(mid) * HARBOR_RADIUS_X) < 118;
      if (harborMouth) continue;
      const wobble0 = Math.sin(i * 4.17) * 0.018;
      const wobble1 = Math.sin((i + 1) * 4.17) * 0.018;
      const inner0 = 0.79 + wobble0;
      const inner1 = 0.79 + wobble1;
      const outer0 = 1.015 + wobble0 * 0.4;
      const outer1 = 1.015 + wobble1 * 0.4;
      const x0i = Math.cos(a0) * HARBOR_RADIUS_X * inner0;
      const z0i = -82 + Math.sin(a0) * HARBOR_RADIUS_Z * inner0;
      const x1i = Math.cos(a1) * HARBOR_RADIUS_X * inner1;
      const z1i = -82 + Math.sin(a1) * HARBOR_RADIUS_Z * inner1;
      const x0o = Math.cos(a0) * HARBOR_RADIUS_X * outer0;
      const z0o = -82 + Math.sin(a0) * HARBOR_RADIUS_Z * outer0;
      const x1o = Math.cos(a1) * HARBOR_RADIUS_X * outer1;
      const z1o = -82 + Math.sin(a1) * HARBOR_RADIUS_Z * outer1;
      const base = positions.length / 3;
      positions.push(
        x0i, this.harborTerrainHeight(x0i, z0i) + 0.18, z0i,
        x1i, this.harborTerrainHeight(x1i, z1i) + 0.18, z1i,
        x0o, -4.2 + Math.sin(i * 1.31) * 0.65, z0o,
        x1o, -4.2 + Math.sin((i + 1) * 1.31) * 0.65, z1o,
      );
      const tint = cliffColors[i % cliffColors.length];
      for (let vertex = 0; vertex < 4; vertex += 1) colors.push(tint.r, tint.g, tint.b);
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const cliffs = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      roughness: 1,
      flatShading: true,
    }));
    cliffs.castShadow = true;
    cliffs.receiveShadow = true;
    this.harborGroup.add(cliffs);
  }

  private harborTerrainHeight(x: number, z: number): number {
    const nx = x / HARBOR_RADIUS_X;
    const nz = (z + 82) / HARBOR_RADIUS_Z;
    const irregularity = Math.sin(x * 0.041 + z * 0.019) * 0.026 + Math.cos(x * 0.017 - z * 0.053) * 0.018;
    const edge = 1 - Math.sqrt(nx * nx + nz * nz) + irregularity;
    if (edge <= 0) return -22 + edge * 5;
    const islandMass = Math.pow(THREE.MathUtils.clamp(edge, 0, 1), 1.72) * 25;
    const keepHill = Math.exp(-(((x - 30) * (x - 30)) / 3800 + ((z + 168) * (z + 168)) / 2700)) * 39;
    const detail = (Math.sin(x * 0.083 + z * 0.031) * 1.02 + Math.cos(x * 0.026 - z * 0.067) * 0.78) * Math.min(1, edge * 4);
    return Math.max(-0.8, islandMass + keepHill + detail);
  }

  private createTerrainDetailTexture(): THREE.DataTexture {
    const size = 256;
    const pixels = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const grain = Math.sin(x * 0.049 + y * 0.071) * 0.52 + Math.sin(x * 0.021 - y * 0.029) * 0.31 + Math.cos(x * 0.013 + y * 0.017) * 0.17;
        const speckle = Math.sin((x * 12.9898 + y * 78.233)) * 43758.5453;
        const random = (speckle - Math.floor(speckle)) - 0.5;
        const value = THREE.MathUtils.clamp(205 + grain * 16 + random * 8, 174, 228);
        const index = (y * size + x) * 4;
        pixels[index] = value;
        pixels[index + 1] = value * 0.99;
        pixels[index + 2] = value * 0.92;
        pixels[index + 3] = 255;
      }
    }
    const texture = new THREE.DataTexture(pixels, size, size, THREE.RGBAFormat);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 3.5);
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    texture.needsUpdate = true;
    return texture;
  }

  private buildHarborCity(): void {
    const wallMaterial = new THREE.MeshStandardMaterial({ color: 0x555954, roughness: 1, flatShading: true });
    const roadMaterial = new THREE.MeshStandardMaterial({ color: 0x605847, roughness: 1 });

    const addWall = (from: [number, number], to: [number, number], height: number, thickness = 3.2): void => {
      const dx = to[0] - from[0];
      const dz = to[1] - from[1];
      const length = Math.hypot(dx, dz);
      const x = (from[0] + to[0]) * 0.5;
      const z = (from[1] + to[1]) * 0.5;
      const wall = new THREE.Mesh(new THREE.BoxGeometry(thickness, height, length), wallMaterial);
      wall.position.set(x, (this.harborTerrainHeight(from[0], from[1]) + this.harborTerrainHeight(to[0], to[1])) * 0.5 + height * 0.32, z);
      wall.rotation.y = Math.atan2(dx, dz);
      wall.castShadow = true;
      wall.receiveShadow = true;
      this.harborGroup.add(wall);
    };
    const addRoad = (from: [number, number], to: [number, number], width: number): void => {
      const dx = to[0] - from[0];
      const dz = to[1] - from[1];
      const length = Math.hypot(dx, dz);
      const segmentCount = Math.max(2, Math.ceil(length / 5));
      const yaw = Math.atan2(dx, dz);
      for (let index = 0; index < segmentCount; index += 1) {
        const t = (index + 0.5) / segmentCount;
        const x = THREE.MathUtils.lerp(from[0], to[0], t);
        const z = THREE.MathUtils.lerp(from[1], to[1], t);
        const road = new THREE.Mesh(new THREE.BoxGeometry(width, 0.34, length / segmentCount * 1.08), roadMaterial);
        road.position.set(x, this.harborTerrainHeight(x, z) + 0.14, z);
        road.rotation.y = yaw;
        road.receiveShadow = true;
        this.harborGroup.add(road);
      }
    };

    // Stone defenses establish the keep's true footprint. The authored keep,
    // guildhall, and towers are loaded into this ward at real-world heights.
    addWall([-26, -142], [73, -142], 8, 4.4);
    addWall([73, -142], [78, -195], 9, 4.4);
    addWall([78, -195], [-25, -202], 10, 4.4);
    addWall([-25, -202], [-26, -142], 8, 4.4);
    addWall([-91, -105], [93, -105], 5.5, 3.2);
    addWall([-119, -58], [112, -58], 4.4, 2.8);

    const roads: Array<[[number, number], [number, number], number]> = [
      [[0, 43], [-11, 10], 6], [[-11, 10], [24, -25], 5.5], [[24, -25], [-14, -63], 5],
      [[-14, -63], [23, -101], 4.8], [[23, -101], [45, -142], 4.5],
      [[-88, 20], [-54, -18], 4.4], [[-54, -18], [-72, -57], 4.1],
      [[72, 18], [60, -20], 4.4], [[60, -20], [83, -56], 4.1],
    ];
    roads.forEach(([from, to, width]) => addRoad(from, to, width));

    // Dense, scale-consistent foliage frames the settlement without blocking
    // its streets. These instanced trees are deliberately secondary to the
    // user's authored architecture and cost only three draw calls.
    const trunkGeometry = new THREE.CylinderGeometry(0.42, 0.7, 5.8, 7);
    const crownGeometry = new THREE.IcosahedronGeometry(3.45, 1);
    const coniferGeometry = new THREE.ConeGeometry(3.3, 9.2, 9, 2);
    const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x33271e, roughness: 1 });
    const crownMaterial = new THREE.MeshStandardMaterial({ color: 0x1f4434, roughness: 1, flatShading: true });
    const coniferMaterial = new THREE.MeshStandardMaterial({ color: 0x173a31, roughness: 1, flatShading: true });
    const trunks = new THREE.InstancedMesh(trunkGeometry, trunkMaterial, 120);
    const crowns = new THREE.InstancedMesh(crownGeometry, crownMaterial, 240);
    const conifers = new THREE.InstancedMesh(coniferGeometry, coniferMaterial, 120);
    const dummy = new THREE.Object3D();
    let treeCount = 0;
    let crownCount = 0;
    let coniferCount = 0;
    for (let i = 0; i < 150 && treeCount < 120; i += 1) {
      const x = -178 + ((i * 73) % 357) + Math.sin(i * 4.17) * 5;
      const z = -238 + ((i * 97) % 267) + Math.cos(i * 2.91) * 4;
      const nearTown = z > -118 && z < 34 && Math.abs(x) < 118;
      const inKeep = x > -35 && x < 84 && z < -132 && z > -210;
      if ((nearTown && i % 4 !== 0) || inKeep || this.harborTerrainHeight(x, z) < 1) continue;
      const heightScale = 0.78 + (i % 6) * 0.075;
      const y = this.harborTerrainHeight(x, z);
      dummy.position.set(x, y + 2.9 * heightScale, z);
      dummy.rotation.set(0, i * 0.73, 0);
      dummy.scale.set(0.82 * heightScale, heightScale, 0.82 * heightScale);
      dummy.updateMatrix();
      trunks.setMatrixAt(treeCount, dummy.matrix);
      if (i % 3 === 0) {
        dummy.position.set(x, y + 7.9 * heightScale, z);
        dummy.scale.set(0.88 * heightScale, 1.18 * heightScale, 0.88 * heightScale);
        dummy.updateMatrix();
        conifers.setMatrixAt(coniferCount, dummy.matrix);
        coniferCount += 1;
      } else {
        dummy.position.set(x - 1.05 * heightScale, y + 7.1 * heightScale, z + 0.25 * heightScale);
        dummy.scale.set(0.85 * heightScale, 1.02 * heightScale, 0.85 * heightScale);
        dummy.updateMatrix();
        crowns.setMatrixAt(crownCount, dummy.matrix);
        crownCount += 1;
        dummy.position.set(x + 1.25 * heightScale, y + 7.65 * heightScale, z - 0.45 * heightScale);
        dummy.scale.set(0.72 * heightScale, 0.92 * heightScale, 0.72 * heightScale);
        dummy.updateMatrix();
        crowns.setMatrixAt(crownCount, dummy.matrix);
        crownCount += 1;
      }
      treeCount += 1;
    }
    trunks.count = treeCount;
    crowns.count = crownCount;
    conifers.count = coniferCount;
    trunks.castShadow = true;
    crowns.castShadow = true;
    conifers.castShadow = true;
    this.harborGroup.add(trunks, crowns, conifers);

    if (false) {
    const stone = new THREE.MeshStandardMaterial({ color: 0x66665f, roughness: 0.96, flatShading: true });
    const darkStone = new THREE.MeshStandardMaterial({ color: 0x3f4745, roughness: 1, flatShading: true });
    const roofMaterials = [0x3a302c, 0x44352e, 0x29383a].map((color) => new THREE.MeshStandardMaterial({ color, roughness: 0.92 }));
    const timber = new THREE.MeshStandardMaterial({ color: 0x31251d, roughness: 0.95 });
    const plaster = new THREE.MeshStandardMaterial({ color: 0xb4ac92, roughness: 1 });
    const windowLight = new THREE.MeshStandardMaterial({ color: 0xffb75d, emissive: 0xff7b2c, emissiveIntensity: 2.3, roughness: 0.45 });
    const bannerPaths = [
      '/assets/textures/banner-free-captains.png',
      '/assets/textures/banner-verdant-court.png',
      '/assets/textures/banner-iron-compact.png',
    ];

    const keep = new THREE.Group();
    const keepX = 34;
    const keepZ = -171;
    const keepY = this.harborTerrainHeight(keepX, keepZ);
    const foundation = new THREE.Mesh(new THREE.CylinderGeometry(39, 49, 12, 9), darkStone);
    foundation.position.y = 2;
    foundation.scale.z = 0.78;
    foundation.castShadow = true;
    foundation.receiveShadow = true;
    keep.add(foundation);

    const ward = new THREE.Mesh(new THREE.BoxGeometry(62, 12, 42), stone);
    ward.position.y = 11;
    ward.castShadow = true;
    ward.receiveShadow = true;
    keep.add(ward);

    const towerPositions = [[-28, -18], [28, -18], [-28, 18], [28, 18]];
    for (const [x, z] of towerPositions) {
      const tower = new THREE.Mesh(new THREE.CylinderGeometry(7.4, 8.6, 25, 10), darkStone);
      tower.position.set(x, 15, z);
      tower.castShadow = true;
      tower.receiveShadow = true;
      keep.add(tower);
      for (let i = 0; i < 10; i += 1) {
        const merlon = new THREE.Mesh(new THREE.BoxGeometry(2.4, 2.5, 2.4), stone);
        const angle = i / 10 * Math.PI * 2;
        merlon.position.set(x + Math.sin(angle) * 7.2, 28, z + Math.cos(angle) * 7.2);
        merlon.rotation.y = angle;
        merlon.castShadow = true;
        keep.add(merlon);
      }
    }

    const greatTower = new THREE.Mesh(new THREE.BoxGeometry(22, 38, 20), darkStone);
    greatTower.position.set(1, 29, -3);
    greatTower.castShadow = true;
    keep.add(greatTower);
    for (let x = -9; x <= 11; x += 5) {
      for (const z of [-11, 5]) {
        const merlon = new THREE.Mesh(new THREE.BoxGeometry(2.8, 3.2, 2.8), stone);
        merlon.position.set(x, 49.5, z);
        keep.add(merlon);
      }
    }
    for (let z = -7; z <= 1; z += 5) {
      for (const x of [-11, 13]) {
        const merlon = new THREE.Mesh(new THREE.BoxGeometry(2.8, 3.2, 2.8), stone);
        merlon.position.set(x, 49.5, z);
        keep.add(merlon);
      }
    }

    bannerPaths.forEach((path, index) => {
      const texture = new THREE.TextureLoader().load(path);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
      const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: true });
      const banner = new THREE.Mesh(new THREE.PlaneGeometry(5.6, 14.4), material);
      banner.position.set((index - 1) * 6.4, 33, 7.08);
      keep.add(banner);
    });
    const beacon = new THREE.Mesh(new THREE.SphereGeometry(1.4, 12, 8), new THREE.MeshBasicMaterial({ color: 0xffc66d }));
    beacon.position.set(1, 52.5, -3);
    keep.add(beacon);
    keep.position.set(keepX, keepY - 4, keepZ);
    this.harborGroup.add(keep);

    const terraceWalls = [
      { x: 15, z: -118, w: 138, yaw: -0.08 },
      { x: -20, z: -72, w: 118, yaw: 0.08 },
      { x: 52, z: -28, w: 72, yaw: -0.16 },
    ];
    for (const item of terraceWalls) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(item.w, 5.5, 3.2), darkStone);
      wall.position.set(item.x, this.harborTerrainHeight(item.x, item.z) - 0.2, item.z);
      wall.rotation.y = item.yaw;
      wall.castShadow = true;
      wall.receiveShadow = true;
      this.harborGroup.add(wall);
    }

    const townSites = [
      [-83, -16, 0.9], [-54, -24, 1.15], [-19, -22, 0.86], [20, -20, 1.08], [61, -17, 0.9],
      [-96, -54, 0.82], [-62, -59, 1.0], [-27, -62, 0.92], [11, -66, 1.12], [56, -62, 0.82], [92, -51, 0.96],
      [-67, -98, 0.78], [-31, -104, 0.94], [8, -109, 0.78], [67, -104, 0.9],
    ];
    townSites.forEach(([x, z, scale], index) => {
      const house = new THREE.Group();
      const body = new THREE.Mesh(new THREE.BoxGeometry(12, 9, 9), plaster);
      body.position.y = 4.5;
      body.castShadow = true;
      body.receiveShadow = true;
      house.add(body);
      const roof = new THREE.Mesh(new THREE.ConeGeometry(8.6, 5.5, 4), roofMaterials[index % roofMaterials.length]);
      roof.position.y = 11;
      roof.rotation.y = Math.PI / 4;
      roof.scale.z = 0.72;
      roof.castShadow = true;
      house.add(roof);
      for (const beamX of [-4.4, 0, 4.4]) {
        const beam = new THREE.Mesh(new THREE.BoxGeometry(0.5, 8.7, 0.45), timber);
        beam.position.set(beamX, 4.6, 4.7);
        house.add(beam);
      }
      const window = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 2.2), windowLight);
      window.position.set(2.2, 5, 4.73);
      house.add(window);
      house.position.set(x, this.harborTerrainHeight(x, z), z);
      house.rotation.y = Math.sin(index * 8.13) * 0.28;
      house.scale.setScalar(scale);
      this.harborGroup.add(house);
    });

    const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x3a2b20, roughness: 1 });
    const canopyMaterials = [0x244538, 0x2f503d, 0x1c392f].map((color) => new THREE.MeshStandardMaterial({ color, roughness: 1, flatShading: true }));
    for (let i = 0; i < 38; i += 1) {
      const angle = i * 2.399;
      const radius = 62 + (i % 7) * 18;
      const x = keepX + Math.sin(angle) * radius;
      const z = keepZ + Math.cos(angle) * radius * 0.58;
      if (z > -18 || (x > -105 && x < 102 && z > -118)) continue;
      const tree = new THREE.Group();
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.8, 6.5, 7), trunkMaterial);
      trunk.position.y = 3.2;
      trunk.castShadow = true;
      const crown = new THREE.Mesh(new THREE.IcosahedronGeometry(4.7 + (i % 3), 0), canopyMaterials[i % canopyMaterials.length]);
      crown.position.y = 8.1;
      crown.scale.set(0.9, 1.25, 0.9);
      crown.castShadow = true;
      tree.add(trunk, crown);
      tree.position.set(x, this.harborTerrainHeight(x, z), z);
      this.harborGroup.add(tree);
    }

    const stairMaterial = new THREE.MeshStandardMaterial({ color: 0x5b5b54, roughness: 1 });
    for (let i = 0; i < 24; i += 1) {
      const t = i / 23;
      const x = THREE.MathUtils.lerp(-2, 28, t);
      const z = THREE.MathUtils.lerp(-42, -143, t);
      const step = new THREE.Mesh(new THREE.BoxGeometry(7, 0.65, 4.7), stairMaterial);
      step.position.set(x, this.harborTerrainHeight(x, z) + 0.25, z);
      step.rotation.y = -0.28;
      step.castShadow = true;
      step.receiveShadow = true;
      this.harborGroup.add(step);
    }
    }
  }

  private fitHarborModelToHeight(model: THREE.Object3D, height: number): void {
    const initialBox = new THREE.Box3().setFromObject(model);
    const initialSize = initialBox.getSize(new THREE.Vector3());
    model.scale.multiplyScalar(height / Math.max(initialSize.y, 0.001));
    const scaledBox = new THREE.Box3().setFromObject(model);
    const center = scaledBox.getCenter(new THREE.Vector3());
    model.position.set(-center.x, -scaledBox.min.y, -center.z);
  }

  private async loadHarborModels(): Promise<void> {
    const placements = [
      { url: '/assets/models/harbor-warehouse.glb', position: new THREE.Vector3(-65, -2.4, 52), height: 17, yaw: Math.PI },
      { url: '/assets/models/harbor-lighthouse.glb', position: new THREE.Vector3(-148, 0.4, -47), height: 38, yaw: 0.4 },
    ];
    await Promise.all(placements.map(async (item) => {
      try {
        const gltf = await this.loader.loadAsync(item.url);
        const model = gltf.scene;
        this.fitHarborModelToHeight(model, item.height);
        model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        const anchor = new THREE.Group();
        anchor.position.copy(item.position);
        anchor.rotation.y = item.yaw;
        anchor.add(model);
        this.harborGroup.add(anchor);
      } catch {
        this.harborGroup.add(this.createHarborFallback(item.position, item.height, item.url.includes('lighthouse')));
      }
    }));

    await this.loadHarborSettlementModels();
    await this.loadHarborRockOutcrops();

    const lanternMaterial = new THREE.MeshBasicMaterial({ color: 0xffc66d });
    const lanternPositions = [
      ...[44, 62, 80, 104].flatMap((z) => [new THREE.Vector3(6.7, 4.6, z), new THREE.Vector3(13.3, 4.6, z)]),
      new THREE.Vector3(-23, 4.6, 62), new THREE.Vector3(-17, 4.6, 62),
      new THREE.Vector3(42, 4.6, 60), new THREE.Vector3(48, 4.6, 60),
    ];
    for (const position of lanternPositions) {
      const lantern = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), lanternMaterial);
      lantern.position.copy(position);
      this.harborGroup.add(lantern);
    }
  }

  private async loadHarborSettlementModels(): Promise<void> {
    type Placement = { url: string; x: number; z: number; height: number; yaw: number };
    const modelRoot = '/assets/models/harbor/';
    const placements: Placement[] = [
      { url: `${modelRoot}blackwake-keep.glb`, x: 31, z: -174, height: 54, yaw: 0.55 },
      { url: `${modelRoot}harbor-guildhall.glb`, x: 15, z: -146, height: 27, yaw: -0.06 },
      { url: `${modelRoot}harbor-round-tower.glb`, x: -18, z: -169, height: 25, yaw: 0.16 },
      { url: `${modelRoot}harbor-round-tower.glb`, x: 70, z: -169, height: 27, yaw: -0.31 },

      { url: `${modelRoot}harbor-house-grand.glb`, x: -36, z: -111, height: 19, yaw: 0.22 },
      { url: `${modelRoot}harbor-house-tall.glb`, x: 1, z: -108, height: 17, yaw: -0.18 },
      { url: `${modelRoot}harbor-houses-cluster.glb`, x: 49, z: -108, height: 15, yaw: 0.14 },
      { url: `${modelRoot}harbor-house-green.glb`, x: -78, z: -94, height: 12, yaw: 0.38 },
      { url: `${modelRoot}harbor-houses-row.glb`, x: 83, z: -87, height: 14, yaw: -0.31 },
      { url: `${modelRoot}harbor-house-tall.glb`, x: -99, z: -72, height: 16, yaw: 0.08 },
      { url: `${modelRoot}harbor-office.glb`, x: 6, z: -70, height: 13, yaw: -0.11 },
      { url: `${modelRoot}harbor-house-wharf.glb`, x: -43, z: -63, height: 12, yaw: 0.26 },
      { url: `${modelRoot}harbor-tavern.glb`, x: 50, z: -57, height: 13, yaw: -0.24 },
      { url: `${modelRoot}harbor-house-grand.glb`, x: -76, z: -43, height: 18, yaw: -0.27 },
      { url: `${modelRoot}harbor-houses-row.glb`, x: -12, z: -37, height: 13, yaw: 0.18 },
      { url: `${modelRoot}harbor-houses-cluster.glb`, x: 33, z: -30, height: 13, yaw: -0.18 },
      { url: `${modelRoot}harbor-house-tall.glb`, x: 78, z: -29, height: 17, yaw: 0.31 },
      { url: `${modelRoot}harbor-house-green.glb`, x: -106, z: -22, height: 12, yaw: -0.15 },
      { url: `${modelRoot}harbor-house-wharf.glb`, x: -61, z: -11, height: 12, yaw: 0.12 },
      { url: `${modelRoot}harbor-tavern.glb`, x: -17, z: 1, height: 13, yaw: -0.08 },
      { url: `${modelRoot}harbor-office.glb`, x: 30, z: 5, height: 12, yaw: 0.2 },
      { url: `${modelRoot}harbor-house-wharf.glb`, x: 72, z: 10, height: 11.5, yaw: -0.3 },
      { url: `${modelRoot}harbor-houses-row.glb`, x: -91, z: 18, height: 13, yaw: 0.27 },
      { url: `${modelRoot}harbor-houses-cluster.glb`, x: 99, z: -4, height: 12.5, yaw: -0.22 },
      { url: `${modelRoot}harbor-house-grand.glb`, x: -128, z: -122, height: 16, yaw: 0.34 },
      { url: `${modelRoot}harbor-house-green.glb`, x: 111, z: -121, height: 12, yaw: -0.28 },
      { url: `${modelRoot}harbor-houses-row.glb`, x: -116, z: -94, height: 13, yaw: 0.12 },
      { url: `${modelRoot}harbor-tavern.glb`, x: -58, z: -84, height: 12.5, yaw: -0.22 },
      { url: `${modelRoot}harbor-house-wharf.glb`, x: 27, z: -87, height: 11.5, yaw: 0.29 },
      { url: `${modelRoot}harbor-office.glb`, x: 109, z: -58, height: 12, yaw: -0.18 },
      { url: `${modelRoot}harbor-house-tall.glb`, x: -132, z: -46, height: 15, yaw: 0.21 },
      { url: `${modelRoot}harbor-houses-cluster.glb`, x: -126, z: 2, height: 11.5, yaw: -0.26 },
      { url: `${modelRoot}harbor-house-grand.glb`, x: 111, z: 23, height: 15, yaw: 0.26 },
      { url: `${modelRoot}harbor-house-tall.glb`, x: 54, z: 32, height: 14, yaw: -0.19 },
      { url: `${modelRoot}harbor-house-wharf.glb`, x: 4, z: 31, height: 11, yaw: 0.08 },
      { url: `${modelRoot}harbor-tavern.glb`, x: -45, z: 31, height: 12, yaw: -0.16 },
    ];

    const sources = new Map<string, THREE.Object3D>();
    await Promise.all([...new Set(placements.map((item) => item.url))].map(async (url) => {
      try {
        const gltf = await this.loader.loadAsync(url);
        sources.set(url, gltf.scene);
      } catch (error) {
        console.warn(`Unable to load harbor asset ${url}`, error);
      }
    }));

    for (const item of placements) {
      const source = sources.get(item.url);
      if (!source) continue;
      const model = source.clone(true);
      this.fitHarborModelToHeight(model, item.height);
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          child.frustumCulled = true;
        }
      });
      const anchor = new THREE.Group();
      anchor.position.set(item.x, this.harborTerrainHeight(item.x, item.z), item.z);
      anchor.rotation.y = item.yaw;
      anchor.add(model);
      this.harborGroup.add(anchor);
    }
  }

  private async loadHarborRockOutcrops(): Promise<void> {
    try {
      const geometry = await new STLLoader().loadAsync('/assets/models/harbor/rock-outcrop.stl');
      geometry.computeBoundingBox();
      const box = geometry.boundingBox!;
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const unitScale = 1 / Math.max(size.x, size.y, 0.001);
      geometry.translate(-center.x, -box.min.y, -center.z);
      geometry.scale(unitScale, unitScale, unitScale);
      geometry.computeVertexNormals();

      const material = new THREE.MeshStandardMaterial({ color: 0x46504c, roughness: 1, flatShading: true });
      const rocks = new THREE.InstancedMesh(geometry, material, 150);
      const dummy = new THREE.Object3D();
      let count = 0;
      for (let i = 0; i < 82; i += 1) {
        const angle = i / 82 * Math.PI * 2;
        const x = Math.cos(angle) * (HARBOR_RADIUS_X - 5 + Math.sin(i * 4.17) * 8);
        const z = -82 + Math.sin(angle) * (HARBOR_RADIUS_Z - 6 + Math.cos(i * 2.63) * 7);
        if (z > 30 && Math.abs(x) < 116) continue;
        const scale = 10 + (i % 6) * 1.8;
        dummy.position.set(x, -1.1 + (i % 3) * 0.36, z);
        dummy.rotation.set(Math.sin(i * 1.7) * 0.12, angle + i * 0.47, Math.cos(i * 1.23) * 0.1);
        dummy.scale.set(scale * (0.84 + (i % 4) * 0.08), scale * (0.62 + (i % 5) * 0.07), scale);
        dummy.updateMatrix();
        rocks.setMatrixAt(count, dummy.matrix);
        count += 1;
      }
      for (let i = 0; i < 62; i += 1) {
        const angle = i * 2.399;
        const radius = 48 + (i % 7) * 9;
        const x = 30 + Math.sin(angle) * radius;
        const z = -166 + Math.cos(angle) * radius * 0.72;
        const inWard = x > -27 && x < 76 && z < -138 && z > -202;
        if (inWard || (z > -92 && Math.abs(x) < 112)) continue;
        const scale = 7 + (i % 5) * 1.35;
        dummy.position.set(x, this.harborTerrainHeight(x, z) - 0.65, z);
        dummy.rotation.set(Math.sin(i) * 0.16, i * 0.93, Math.cos(i * 1.2) * 0.13);
        dummy.scale.set(scale * 0.92, scale * (0.58 + (i % 4) * 0.08), scale);
        dummy.updateMatrix();
        rocks.setMatrixAt(count, dummy.matrix);
        count += 1;
      }
      rocks.count = count;
      rocks.castShadow = true;
      rocks.receiveShadow = true;
      this.harborGroup.add(rocks);
    } catch (error) {
      console.warn('Unable to load the authored harbor rock outcrop.', error);
    }
  }

  private createHarborFallback(position: THREE.Vector3, scale: number, lighthouse: boolean): THREE.Group {
    const group = new THREE.Group();
    group.position.copy(position);
    if (lighthouse) {
      const tower = new THREE.Mesh(
        new THREE.CylinderGeometry(scale * 0.08, scale * 0.13, scale * 0.75, 12),
        new THREE.MeshStandardMaterial({ color: 0xc1b59b, roughness: 0.86 }),
      );
      tower.position.y = scale * 0.34;
      tower.castShadow = true;
      const lamp = new THREE.Mesh(new THREE.SphereGeometry(scale * 0.075, 12, 8), new THREE.MeshBasicMaterial({ color: 0xffdf91 }));
      lamp.position.y = scale * 0.76;
      group.add(tower, lamp);
    } else {
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(scale * 0.7, scale * 0.3, scale * 0.4),
        new THREE.MeshStandardMaterial({ color: 0x5a4434, roughness: 0.9 }),
      );
      body.position.y = scale * 0.15;
      body.castShadow = true;
      const roof = new THREE.Mesh(
        new THREE.ConeGeometry(scale * 0.48, scale * 0.22, 4),
        new THREE.MeshStandardMaterial({ color: 0x263235, roughness: 0.85 }),
      );
      roof.position.y = scale * 0.42;
      roof.rotation.y = Math.PI / 4;
      roof.castShadow = true;
      group.add(body, roof);
    }
    return group;
  }

  private buildClouds(): void {
    const textures = [0, 1, 2].map((variant) => {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 256;
      const context = canvas.getContext('2d')!;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.filter = 'blur(13px)';
      for (let i = 0; i < 16; i += 1) {
        const x = 54 + ((i * 79 + variant * 41) % 410);
        const y = 116 + Math.sin(i * 1.67 + variant) * 28;
        const rx = 38 + (i % 5) * 9;
        const ry = 20 + (i % 4) * 7;
        const gradient = context.createRadialGradient(x, y, 2, x, y, rx);
        gradient.addColorStop(0, 'rgba(250,252,248,.84)');
        gradient.addColorStop(0.52, 'rgba(238,244,241,.54)');
        gradient.addColorStop(1, 'rgba(220,232,230,0)');
        context.fillStyle = gradient;
        context.beginPath();
        context.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
        context.fill();
      }
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.needsUpdate = true;
      return texture;
    });
    for (let i = 0; i < 15; i += 1) {
      const cloud = new THREE.Sprite(new THREE.SpriteMaterial({
        map: textures[i % textures.length],
        color: i % 4 === 0 ? 0xd5dddc : 0xf0f2ed,
        transparent: true,
        opacity: 0.25 + (i % 3) * 0.045,
        depthWrite: false,
        fog: true,
      }));
      const angle = (i / 15) * Math.PI * 2;
      const radius = 310 + (i % 5) * 75;
      cloud.position.set(Math.sin(angle) * radius, 104 + (i % 5) * 19, Math.cos(angle) * radius);
      cloud.scale.set(150 + (i % 4) * 33, 54 + (i % 3) * 12, 1);
      cloud.userData.speed = 0.32 + (i % 4) * 0.11;
      this.scene.add(cloud);
    }
  }

  private buildEventMarker(): void {
    const lightMaterial = new THREE.MeshBasicMaterial({ color: 0x4cffce, transparent: true, opacity: 0.78 });
    for (let i = 0; i < 14; i += 1) {
      const glow = new THREE.Mesh(new THREE.SphereGeometry(0.8 + (i % 3) * 0.25, 10, 8), lightMaterial);
      const angle = (i / 14) * Math.PI * 2;
      const radius = 6 + (i % 4) * 2.4;
      glow.position.set(Math.sin(angle) * radius, -1.7 - (i % 3) * 0.6, Math.cos(angle) * radius);
      glow.userData.phase = i * 0.61;
      this.eventGroup.add(glow);
    }
    this.eventRing = new THREE.Mesh(
      new THREE.RingGeometry(15, 17, 64),
      new THREE.MeshBasicMaterial({ color: 0x4cffce, transparent: true, opacity: 0.18, side: THREE.DoubleSide }),
    );
    this.eventRing.rotation.x = -Math.PI / 2;
    this.eventRing.position.y = 0.2;
    this.eventGroup.add(this.eventRing);
    this.eventGroup.position.copy(EVENT_POSITION);
    this.eventGroup.visible = false;
    this.scene.add(this.eventGroup);
  }

  private buildLootMarker(): void {
    const crate = new THREE.Mesh(
      new THREE.BoxGeometry(3.3, 2.4, 2.6),
      new THREE.MeshStandardMaterial({ color: 0x6d4a2b, roughness: 0.82, emissive: 0x241405, emissiveIntensity: 0.5 }),
    );
    crate.castShadow = true;
    this.lootGroup.add(crate);
    this.lootRing = new THREE.Mesh(
      new THREE.TorusGeometry(5.4, 0.12, 8, 48),
      new THREE.MeshBasicMaterial({ color: 0xffcf67, transparent: true, opacity: 0.68 }),
    );
    this.lootRing.rotation.x = Math.PI / 2;
    this.lootGroup.add(this.lootRing);
    this.lootGroup.visible = false;
    this.scene.add(this.lootGroup);
  }

  private buildObjectiveBeacon(): void {
    const line = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.55, 55, 10, 1, true),
      new THREE.MeshBasicMaterial({ color: 0xc8a95d, transparent: true, opacity: 0.15, side: THREE.DoubleSide }),
    );
    line.position.y = 27;
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(4.5, 0.18, 8, 64),
      new THREE.MeshBasicMaterial({ color: 0xffd277, transparent: true, opacity: 0.62 }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 2;
    this.objectiveBeacon.add(line, ring);
    this.objectiveBeacon.visible = false;
    this.scene.add(this.objectiveBeacon);
  }

  private async createShip(definition: ShipDefinition, position: THREE.Vector3, heading: number): Promise<ShipState> {
    const root = new THREE.Group();
    root.position.copy(position);
    root.rotation.order = 'YXZ';
    let hasClothMask = false;
    try {
      const gltf = await this.loader.loadAsync(definition.modelUrl);
      const model = gltf.scene;
      this.normalizeModel(model, definition.modelScale);
      model.rotation.y = definition.modelYaw;
      model.position.y += definition.waterline;
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          child.frustumCulled = true;
          const color = child.geometry.getAttribute('color');
          if (color?.itemSize === 4) hasClothMask = true;
        }
      });
      root.add(model);
    } catch {
      root.add(this.createFallbackShip(definition));
    }
    const ship: ShipState = {
      kind: definition.id,
      object: root,
      position: root.position,
      heading,
      speed: 0,
      trim: definition.id === 'cutter' ? 0.34 : 0.82,
      hull: definition.maxHull,
      sails: definition.maxSails,
      portCooldown: 0,
      starboardCooldown: 0,
      blinkCooldown: 0,
      invulnerability: 0,
      alive: true,
    };
    if (hasClothMask) this.installWindCloth(ship);
    return ship;
  }

  private installWindCloth(ship: ShipState): void {
    const uniforms: ClothUniforms = {
      time: { value: 0 },
      wind: { value: new THREE.Vector3(0, 0, 1) },
      strength: { value: 0 },
      flutter: { value: 0 },
    };
    ship.object.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const color = child.geometry.getAttribute('color');
      if (color?.itemSize !== 4) return;
      const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
      const clothMaterials = sourceMaterials.map((source) => {
        if (!(source instanceof THREE.MeshStandardMaterial)) return source;
        const material = source.clone();
        material.vertexColors = true;
        material.onBeforeCompile = (shader) => {
          shader.uniforms.uClothTime = uniforms.time;
          shader.uniforms.uClothWind = uniforms.wind;
          shader.uniforms.uClothStrength = uniforms.strength;
          shader.uniforms.uClothFlutter = uniforms.flutter;
          shader.vertexShader = shader.vertexShader
            .replace('#include <common>', `
              #include <common>
              uniform float uClothTime;
              uniform vec3 uClothWind;
              uniform float uClothStrength;
              uniform float uClothFlutter;
            `)
            .replace('#include <begin_vertex>', `
              vec3 transformed = vec3(position);
              float clothWeight = color.a;
              float flagAmount = smoothstep(0.64, 0.76, position.y);
              float slowBillow = sin(uClothTime * 1.45 + position.y * 15.0 + position.x * 7.0);
              float travelingRipple = sin(
                uClothTime * (4.2 + uClothFlutter * 5.8)
                + position.y * 52.0
                + position.x * 21.0
                + position.z * 17.0
              );
              float snap = sin(uClothTime * 8.7 + position.x * 37.0 - position.z * 31.0);
              float normalOffset = clothWeight * uClothStrength * (
                slowBillow * (0.0045 + flagAmount * 0.005)
                + travelingRipple * (0.0018 + uClothFlutter * 0.0025 + flagAmount * 0.003)
                + snap * flagAmount * uClothFlutter * 0.002
              );
              transformed += objectNormal * normalOffset;
              transformed.xz += uClothWind.xz * clothWeight * uClothStrength * (
                0.0025 + flagAmount * 0.006
              );
            `);
        };
        material.customProgramCacheKey = () => 'blackwake-wind-cloth-v1';
        material.needsUpdate = true;
        return material;
      });
      child.material = Array.isArray(child.material) ? clothMaterials : clothMaterials[0];
    });
    this.clothRigs.set(ship.kind, { ship, uniforms });
  }

  private updateWindCloth(): void {
    const apparentWind = new THREE.Vector3();
    const localWind = new THREE.Vector3();
    const worldQuaternion = new THREE.Quaternion();
    for (const rig of this.clothRigs.values()) {
      const definition = SHIPS[rig.ship.kind];
      const forward = this.forward(rig.ship.heading);
      apparentWind.set(this.wind.x * 14, 0, this.wind.y * 14).addScaledVector(forward, -rig.ship.speed);
      const apparentSpeed = apparentWind.length();
      rig.ship.object.getWorldQuaternion(worldQuaternion);
      localWind.copy(apparentWind).applyQuaternion(worldQuaternion.invert()).normalize();
      const windFactor = relativeWindFactor(rig.ship.heading, this.wind.x, this.wind.y);
      const luff = THREE.MathUtils.clamp((0.7 - windFactor) / 0.36, 0, 1);
      const damageLooseness = 1 - rig.ship.sails / Math.max(definition.maxSails, 1);
      rig.uniforms.time.value = this.elapsed + (rig.ship.kind === 'pirate' ? 1.73 : 0);
      rig.uniforms.wind.value.copy(localWind);
      rig.uniforms.strength.value = THREE.MathUtils.clamp(apparentSpeed / 18, 0.16, 1.25);
      rig.uniforms.flutter.value = THREE.MathUtils.clamp(
        0.12 + luff * (0.38 + rig.ship.trim * 0.5) + damageLooseness * 0.55,
        0.12,
        1.25,
      );
    }
  }

  private createFallbackShip(definition: ShipDefinition): THREE.Group {
    const group = new THREE.Group();
    const hullMaterial = new THREE.MeshStandardMaterial({ color: definition.fallbackColor, roughness: 0.64, metalness: 0.08 });
    const woodMaterial = new THREE.MeshStandardMaterial({ color: definition.id === 'cutter' ? 0xb7a270 : 0x392522, roughness: 0.88 });
    const sailMaterial = new THREE.MeshStandardMaterial({
      color: definition.id === 'cutter' ? 0xe8e1c6 : 0x7e2d2c,
      side: THREE.DoubleSide,
      roughness: 0.8,
    });
    const hull = new THREE.Mesh(new THREE.CapsuleGeometry(3.5, 14, 8, 16), hullMaterial);
    hull.rotation.x = Math.PI / 2;
    hull.scale.set(1, 0.72, 1);
    hull.position.y = 1.1;
    hull.castShadow = true;
    group.add(hull);
    const deck = new THREE.Mesh(new THREE.BoxGeometry(6, 0.8, 15), woodMaterial);
    deck.position.y = 2.3;
    deck.castShadow = true;
    group.add(deck);
    for (const z of [-4.2, 3.8]) {
      const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.28, 14, 8), woodMaterial);
      mast.position.set(0, 9, z);
      mast.castShadow = true;
      group.add(mast);
      const sail = new THREE.Mesh(new THREE.PlaneGeometry(7.5, 7.5), sailMaterial);
      sail.position.set(0.18, 10, z + 0.2);
      sail.rotation.y = 0.06;
      sail.castShadow = true;
      group.add(sail);
    }
    return group;
  }

  private normalizeModel(model: THREE.Object3D, targetSize: number): void {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    // Ship size is authored as real waterline length. Masts and magical sails
    // must not make the hull shrink relative to the harbor architecture.
    const scale = targetSize / Math.max(size.x, size.z, 0.0001);
    model.scale.setScalar(scale);
    model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
  }

  private buildHud(): void {
    this.hudRoot.innerHTML = `
      <div class="hud hidden">
        <div class="hud-top">
          <div class="objective-kicker">Current heading</div>
          <div class="objective-text">Awaiting orders</div>
          <div class="distance-pill">At harbor</div>
        </div>
        <div class="enemy-card hidden"><div class="enemy-name">THE RED WRAITH</div><div class="enemy-meter"><span></span></div></div>
        <div class="status-cluster">
          <div class="ship-name-row"><span class="ship-name">The Moonglass</span><span class="ship-class">Elven cutter</span></div>
          <div class="meter-wrap"><div class="meter-label"><span>Hull integrity</span><span class="hull-text">100 / 100</span></div><div class="meter hull"><span></span></div></div>
          <div class="meter-wrap"><div class="meter-label"><span>Sail strength</span><span class="sails-text">100 / 100</span></div><div class="meter sails"><span></span></div></div>
          <div class="status-divider"></div>
          <div class="ability-row">
            <div class="ability"><div class="ability-head"><span>Port</span><span class="keycap">Q</span></div><div class="cooldown port"><span></span></div></div>
            <div class="ability blink"><div class="ability-head"><span>Blink</span><span class="keycap">Space</span></div><div class="cooldown blink-meter"><span></span></div></div>
            <div class="ability"><div class="ability-head"><span>Stbd.</span><span class="keycap">E</span></div><div class="cooldown starboard"><span></span></div></div>
          </div>
          <div class="resource-row"><span>Cargo <strong class="cargo-count">0</strong></span><span>Gold <strong class="gold-count">0</strong></span><span>Trim <strong class="trim-count">34%</strong></span></div>
        </div>
        <div class="wind-dial"><div class="wind-arrow"></div><div class="speed-readout"><span>0.0</span><small>KNOTS</small></div></div>
        <div class="camera-mode hidden"><span class="keycap">V</span><span>Walk the deck</span></div>
        <div class="deck-reticle hidden"><i></i><i></i></div>
        <div class="context-prompt hidden"><span class="keycap">R</span><span class="context-text">Interact</span></div>
        <div class="vignette"></div><div class="damage-flash"></div><div class="blink-flash"></div>
      </div>`;

    const root = this.hudRoot.querySelector<HTMLElement>('.hud')!;
    this.hud = {
      root,
      objective: root.querySelector<HTMLElement>('.objective-text')!,
      distance: root.querySelector<HTMLElement>('.distance-pill')!,
      hull: root.querySelector<HTMLElement>('.meter.hull span')!,
      hullText: root.querySelector<HTMLElement>('.hull-text')!,
      sails: root.querySelector<HTMLElement>('.meter.sails span')!,
      sailsText: root.querySelector<HTMLElement>('.sails-text')!,
      port: root.querySelector<HTMLElement>('.cooldown.port span')!,
      starboard: root.querySelector<HTMLElement>('.cooldown.starboard span')!,
      blink: root.querySelector<HTMLElement>('.blink-meter span')!,
      cargo: root.querySelector<HTMLElement>('.cargo-count')!,
      gold: root.querySelector<HTMLElement>('.gold-count')!,
      windArrow: root.querySelector<HTMLElement>('.wind-arrow')!,
      speed: root.querySelector<HTMLElement>('.speed-readout span')!,
      trim: root.querySelector<HTMLElement>('.trim-count')!,
      context: root.querySelector<HTMLElement>('.context-prompt')!,
      contextText: root.querySelector<HTMLElement>('.context-text')!,
      cameraMode: root.querySelector<HTMLElement>('.camera-mode')!,
      deckReticle: root.querySelector<HTMLElement>('.deck-reticle')!,
      enemy: root.querySelector<HTMLElement>('.enemy-card')!,
      enemyHull: root.querySelector<HTMLElement>('.enemy-meter span')!,
      damageFlash: root.querySelector<HTMLElement>('.damage-flash')!,
      blinkFlash: root.querySelector<HTMLElement>('.blink-flash')!,
    };
  }

  private bindInputs(): void {
    const unlockAudio = (): void => { void this.audio.unlock(); };
    window.addEventListener('pointerdown', unlockAudio, { capture: true });
    window.addEventListener('touchstart', unlockAudio, { capture: true, passive: true });
    window.addEventListener('keydown', unlockAudio, { capture: true });
    window.addEventListener('resize', this.resize);
    window.addEventListener('keydown', (event) => {
      const key = event.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'q', 'e', ' ', 'r', 'f', 'c', 'v'].includes(key)) event.preventDefault();
      if (event.repeat && ['q', 'e', ' ', 'r', 'f', 'c', 'v', 'escape'].includes(key)) return;
      this.keys.add(key);
      if (key === 'enter' && this.mode === 'event') {
        this.resolveEvent(true);
        return;
      }
      if (key === 'enter' && this.mode === 'sunk') {
        this.respawnAtHarbor();
        return;
      }
      if (key === 'enter' && this.testMode && this.mode === 'upgrade') {
        this.prepareNextVoyage();
        return;
      }
      if (key === 'b' && this.testMode && this.mode === 'upgrade') {
        this.purchaseUpgrade('quickfuse');
        return;
      }
      if (key === 'q') this.fireBroadside(this.player, 'port');
      if (key === 'e') this.fireBroadside(this.player, 'starboard');
      if (key === 'b' && this.testMode) this.fireBroadside(this.player, 'starboard');
      if (key === ' ') this.blink();
      if (key === 'r' || key === 'enter') this.interact();
      if (key === 'c') {
        this.battleCameraEnabled = !this.battleCameraEnabled;
        this.toast(this.battleCameraEnabled ? 'Battle camera armed.' : 'Captain camera: drag to orbit, wheel to zoom.');
      }
      if (key === 'v') this.toggleDeckView();
      if (key === 'f') void this.toggleFullscreen();
      if (key === 'escape') this.togglePause();
      if (this.testMode) this.handleDebugKey(key);
    });
    window.addEventListener('keyup', (event) => this.keys.delete(event.key.toLowerCase()));
    window.addEventListener('blur', () => this.keys.clear());

    this.canvas.addEventListener('pointerdown', (event) => {
      this.draggingCamera = true;
      this.lastPointer.set(event.clientX, event.clientY);
      this.canvas.setPointerCapture(event.pointerId);
    });
    this.canvas.addEventListener('pointermove', (event) => {
      if (!this.draggingCamera) return;
      const dx = event.clientX - this.lastPointer.x;
      const dy = event.clientY - this.lastPointer.y;
      this.lastPointer.set(event.clientX, event.clientY);
      if (this.cameraView === 'deck') {
        this.deckYaw -= dx * 0.0042;
        this.deckPitch = THREE.MathUtils.clamp(this.deckPitch - dy * 0.0035, -0.75, 0.72);
      } else {
        this.cameraYaw -= dx * 0.004;
        this.cameraPitch = THREE.MathUtils.clamp(this.cameraPitch + dy * 0.003, 0.08, 0.5);
      }
    });
    this.canvas.addEventListener('pointerup', (event) => {
      this.draggingCamera = false;
      this.canvas.releasePointerCapture(event.pointerId);
    });
    this.canvas.addEventListener('wheel', (event) => {
      event.preventDefault();
      if (this.cameraView === 'deck') return;
      this.cameraDistance = THREE.MathUtils.clamp(this.cameraDistance + event.deltaY * 0.035, 34, 82);
    }, { passive: false });
  }

  private loop = (now: number): void => {
    const frameDelta = Math.min((now - this.lastFrame) / 1000, 0.1);
    this.lastFrame = now;
    if (!this.testMode) {
      this.accumulator += frameDelta;
      while (this.accumulator >= FIXED_STEP) {
        this.update(FIXED_STEP);
        this.accumulator -= FIXED_STEP;
      }
    }
    this.render();
    requestAnimationFrame(this.loop);
  };

  private update(dt: number): void {
    this.elapsed += dt;
    const waterMaterial = this.ocean.material as THREE.ShaderMaterial;
    waterMaterial.uniforms.time.value = this.elapsed * 0.48;

    for (const child of this.scene.children) {
      if (child instanceof THREE.Group && child.userData.speed) child.position.x += child.userData.speed * dt;
      if (child.position.x > 900) child.position.x = -900;
    }

    this.animateMarkers(dt);
    this.updateWindCloth();
    if (this.player) this.updateWakeRig(this.player, dt);
    if (this.pirate && this.pirateVisible) this.updateWakeRig(this.pirate, dt);
    if (this.mode === 'paused' || this.mode === 'event' || this.mode === 'results' || this.mode === 'upgrade' || this.mode === 'docked') {
      this.updateEffects(dt);
      this.updateCamera(dt);
      return;
    }
    if (!this.player) return;

    if (['sailing', 'combat', 'loot'].includes(this.mode)) {
      this.updatePlayer(dt);
      if (this.pirateVisible && this.pirate.alive) this.updatePirate(dt);
      this.updateShipPose(this.player, dt);
      if (this.pirateVisible) this.updateShipPose(this.pirate, dt);
      this.updateProjectiles(dt);
      this.updateEffects(dt);
      this.updateEncounters();
      this.resolveCollisions();
      this.spawnWake(dt, this.player);
      if (this.pirateVisible && this.pirate.alive) this.spawnWake(dt, this.pirate);
    }
    if (this.pirateSinking > 0) {
      this.pirateSinking += dt;
      this.pirate.object.rotation.z += dt * 0.2;
      this.pirate.object.position.y -= dt * 0.7;
      if (this.pirateSinking > 5) this.pirate.object.visible = false;
    }
    this.updateCamera(dt);
    this.updateObjective();
    this.updateHud();
  }

  private updatePlayer(dt: number): void {
    const definition = this.playerDefinition();
    const trimDelta = 0.34 * dt;
    if (this.cameraView === 'deck') this.updateDeckMovement(dt);
    if (this.cameraView !== 'deck' && (this.keys.has('w') || this.keys.has('arrowup'))) this.player.trim = Math.min(1, this.player.trim + trimDelta);
    if (this.cameraView !== 'deck' && (this.keys.has('s') || this.keys.has('arrowdown'))) this.player.trim = Math.max(0, this.player.trim - trimDelta);

    const sailRatio = this.player.sails / this.maxPlayerSails();
    const windFactor = this.windFactor(this.player.heading);
    const upgradeSpeed = (this.upgrades.has('runed-rigging') ? 1.1 : 1) * (this.allegiance === 'verdant-court' ? 1.08 : 1);
    const targetSpeed = definition.maxSpeed * upgradeSpeed * this.player.trim * windFactor * (0.35 + sailRatio * 0.65);
    const acceleration = definition.acceleration * (targetSpeed >= this.player.speed ? 1 : 0.75);
    this.player.speed = THREE.MathUtils.damp(this.player.speed, targetSpeed, acceleration / Math.max(definition.maxSpeed, 1), dt);

    const steer = this.cameraView === 'deck' ? 0 : (this.keys.has('a') || this.keys.has('arrowleft') ? 1 : 0) - (this.keys.has('d') || this.keys.has('arrowright') ? 1 : 0);
    const authority = THREE.MathUtils.clamp(Math.abs(this.player.speed) / 7, 0.14, 1);
    this.player.heading += steer * definition.turnRate * authority * dt;

    const forward = this.forward(this.player.heading);
    this.player.position.addScaledVector(forward, this.player.speed * dt);
    this.player.portCooldown = Math.max(0, this.player.portCooldown - dt);
    this.player.starboardCooldown = Math.max(0, this.player.starboardCooldown - dt);
    this.player.blinkCooldown = Math.max(0, this.player.blinkCooldown - dt);
    this.player.invulnerability = Math.max(0, this.player.invulnerability - dt);
  }

  private updateDeckMovement(dt: number): void {
    const forwardInput = (this.keys.has('w') || this.keys.has('arrowup') ? 1 : 0) - (this.keys.has('s') || this.keys.has('arrowdown') ? 1 : 0);
    const rightInput = (this.keys.has('d') || this.keys.has('arrowright') ? 1 : 0) - (this.keys.has('a') || this.keys.has('arrowleft') ? 1 : 0);
    if (!forwardInput && !rightInput) return;
    const forwardX = Math.sin(this.deckYaw);
    const forwardZ = Math.cos(this.deckYaw);
    const rightX = Math.cos(this.deckYaw);
    const rightZ = -Math.sin(this.deckYaw);
    const length = Math.max(1, Math.hypot(forwardInput, rightInput));
    const walkSpeed = 5.2;
    this.deckPosition.x += (forwardX * forwardInput + rightX * rightInput) / length * walkSpeed * dt;
    this.deckPosition.z += (forwardZ * forwardInput + rightZ * rightInput) / length * walkSpeed * dt;
    const beamLimit = 4.8 - Math.max(0, Math.abs(this.deckPosition.z) - 7) * 0.18;
    this.deckPosition.x = THREE.MathUtils.clamp(this.deckPosition.x, -beamLimit, beamLimit);
    this.deckPosition.z = THREE.MathUtils.clamp(this.deckPosition.z, -12, 14);
  }

  private updatePirate(dt: number): void {
    const definition = SHIPS.pirate;
    const toPlayer = this.player.position.clone().sub(this.pirate.position);
    const distance = toPlayer.length();
    const angleToPlayer = Math.atan2(toPlayer.x, toPlayer.z);
    let desiredHeading = angleToPlayer;
    if (distance < 88) desiredHeading = angleToPlayer + this.pirateAiSide * Math.PI * 0.5;
    if (distance < 42) desiredHeading = angleToPlayer + Math.PI;
    const turn = THREE.MathUtils.clamp(this.angleDelta(this.pirate.heading, desiredHeading), -1, 1);
    const authority = THREE.MathUtils.clamp(this.pirate.speed / 5, 0.2, 1);
    this.pirate.heading += turn * definition.turnRate * authority * dt;
    const targetSpeed = distance > 50 ? definition.maxSpeed * 0.82 : definition.maxSpeed * 0.62;
    this.pirate.speed = THREE.MathUtils.damp(this.pirate.speed, targetSpeed, 0.65, dt);
    this.pirate.position.addScaledVector(this.forward(this.pirate.heading), this.pirate.speed * dt);
    this.pirate.portCooldown = Math.max(0, this.pirate.portCooldown - dt);
    this.pirate.starboardCooldown = Math.max(0, this.pirate.starboardCooldown - dt);

    if (distance < 92) {
      const relative = this.angleDelta(this.pirate.heading, angleToPlayer);
      if (Math.abs(relative - Math.PI / 2) < 0.27 || Math.abs(relative + Math.PI * 1.5) < 0.27) this.fireBroadside(this.pirate, 'port');
      if (Math.abs(relative + Math.PI / 2) < 0.27 || Math.abs(relative - Math.PI * 1.5) < 0.27) this.fireBroadside(this.pirate, 'starboard');
    }
    if (Math.abs(this.angleDelta(this.pirate.heading, angleToPlayer)) > 2.5 && distance > 115) this.pirateAiSide *= -1;
  }

  private updateShipPose(ship: ShipState, dt: number): void {
    const forward = this.forward(ship.heading);
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const bow = this.sampleWave(ship.position.x + forward.x * 7, ship.position.z + forward.z * 7, this.elapsed);
    const stern = this.sampleWave(ship.position.x - forward.x * 7, ship.position.z - forward.z * 7, this.elapsed);
    const port = this.sampleWave(ship.position.x - right.x * 3.5, ship.position.z - right.z * 3.5, this.elapsed);
    const starboard = this.sampleWave(ship.position.x + right.x * 3.5, ship.position.z + right.z * 3.5, this.elapsed);
    const center = this.sampleWave(ship.position.x, ship.position.z, this.elapsed);
    ship.position.y = THREE.MathUtils.damp(ship.position.y, center - 0.1, 5.5, dt);
    const pitch = Math.atan2(bow - stern, 14);
    const rollWave = Math.atan2(port - starboard, 7);
    const steerRoll = ship.kind === 'cutter' ? this.angleDelta(ship.object.rotation.y, ship.heading) * 0.1 : 0;
    ship.object.rotation.y = ship.heading;
    ship.object.rotation.x = THREE.MathUtils.damp(ship.object.rotation.x, pitch, 5, dt);
    ship.object.rotation.z = THREE.MathUtils.damp(ship.object.rotation.z, rollWave + steerRoll, 4, dt);
  }

  private sampleWave(x: number, z: number, time: number): number {
    return sampleWaveHeight(x, z, time);
  }

  private fireBroadside(ship: ShipState, side: Side): void {
    if (!ship?.alive || !['sailing', 'combat', 'loot'].includes(this.mode)) return;
    const cooldownKey = side === 'port' ? 'portCooldown' : 'starboardCooldown';
    if (ship[cooldownKey] > 0) return;
    const definition = SHIPS[ship.kind];
    const reloadMultiplier = ship.kind === 'cutter'
      ? (this.upgrades.has('quickfuse') ? 0.85 : 1) * (this.crew === 'mara-vane' ? 0.82 : 1)
      : 1;
    ship[cooldownKey] = definition.reload * reloadMultiplier;
    const forward = this.forward(ship.heading);
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const sideVector = right.multiplyScalar(broadsideSideSign(side));
    this.lastBroadside = {
      owner: ship.kind,
      side,
      x: Number(sideVector.x.toFixed(3)),
      z: Number(sideVector.z.toFixed(3)),
    };
    for (let i = -2; i <= 2; i += 1) {
      const position = ship.position.clone()
        .addScaledVector(sideVector, definition.collisionRadius * 0.7)
        .addScaledVector(forward, i * 2.05);
      position.y += 2.3;
      const spread = (i * 0.004 + (this.random() - 0.5) * 0.018);
      const direction = sideVector.clone().applyAxisAngle(UP, spread);
      const velocity = direction.multiplyScalar(42).addScaledVector(forward, ship.speed * 0.46);
      velocity.y = 4.2 + this.random() * 0.8;
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.34, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0x1b1613 }),
      );
      mesh.position.copy(position);
      this.scene.add(mesh);
      this.projectiles.push({
        id: this.projectileId++,
        owner: ship.kind,
        position: mesh.position,
        velocity,
        life: 3.3,
        damage: definition.cannonDamage * (ship.kind === 'cutter' && this.crew === 'mara-vane' ? 1.12 : 1) / 5,
        object: mesh,
      });
      this.spawnEffect(position, direction.clone().multiplyScalar(4), 'smoke', 0.8);
    }
    if (ship.kind === 'cutter') this.cameraShake = Math.max(this.cameraShake, 0.34);
    void this.audio.unlock().then(() => this.audio.cannon());
  }

  private updateProjectiles(dt: number): void {
    for (let i = this.projectiles.length - 1; i >= 0; i -= 1) {
      const projectile = this.projectiles[i];
      projectile.life -= dt;
      projectile.velocity.y -= 9.8 * dt;
      projectile.position.addScaledVector(projectile.velocity, dt);
      const wave = this.sampleWave(projectile.position.x, projectile.position.z, this.elapsed);
      const target = projectile.owner === 'cutter' ? this.pirate : this.player;
      const canHit = target.alive && (target.kind === 'cutter' || this.pirateVisible);
      const horizontal = Math.hypot(projectile.position.x - target.position.x, projectile.position.z - target.position.z);
      if (canHit && horizontal < SHIPS[target.kind].collisionRadius && projectile.position.y < target.position.y + 5) {
        this.damageShip(target, projectile.damage);
        this.removeProjectile(i);
        continue;
      }
      if (projectile.position.y <= wave || projectile.life <= 0) {
        const hit = projectile.position.clone();
        hit.y = wave;
        this.spawnEffect(hit, new THREE.Vector3(0, 5, 0), 'splash', 1.1);
        this.audio.splash();
        this.removeProjectile(i);
      }
    }
  }

  private removeProjectile(index: number): void {
    const [projectile] = this.projectiles.splice(index, 1);
    this.scene.remove(projectile.object);
    (projectile.object as THREE.Mesh).geometry.dispose();
  }

  private damageShip(ship: ShipState, amount: number): void {
    if (ship.invulnerability > 0 || !ship.alive) return;
    const actualAmount = ship.kind === 'cutter' && this.allegiance === 'iron-compact' ? amount * 0.85 : amount;
    const damaged = applyDamage(ship.hull, ship.sails, actualAmount);
    ship.hull = damaged.hull;
    ship.sails = damaged.sails;
    if (ship.hull > 0) this.spawnFloatingText(ship.position.clone().add(new THREE.Vector3(0, 8, 0)), `−${Math.round(actualAmount)}`);
    this.spawnEffect(ship.position.clone().add(new THREE.Vector3(0, 3, 0)), new THREE.Vector3(0, 3, 0), 'smoke', 1.2);
    this.audio.hit();
    if (ship.kind === 'cutter') {
      this.cameraShake = Math.max(this.cameraShake, 0.65);
      this.hud.damageFlash.classList.add('active');
      window.setTimeout(() => this.hud.damageFlash.classList.remove('active'), 110);
    }
    if (ship.hull <= 0) {
      ship.alive = false;
      ship.speed = 0;
      if (ship.kind === 'pirate') this.defeatPirate();
      else this.sinkPlayer();
    }
  }

  private blink(): void {
    if (!this.player?.alive || !['sailing', 'combat', 'loot'].includes(this.mode) || this.player.blinkCooldown > 0) return;
    const cooldown = this.playerBlinkCooldown();
    const forward = this.forward(this.player.heading);
    const start = this.player.position.clone();
    const destination = start.clone().addScaledVector(forward, this.crew === 'ilyra-moss' ? 36 : 30);
    if (!isOutsideCircle(destination.x, destination.z, HARBOR_CENTER.x, HARBOR_CENTER.z, 62)) {
      this.toast('The harbor wards turn the blink aside.', 'magic');
      return;
    }
    if (this.pirateVisible && this.pirate.alive && destination.distanceTo(this.pirate.position) < 15) {
      destination.copy(this.pirate.position).addScaledVector(forward, -15);
    }
    for (let i = 0; i < 12; i += 1) {
      const point = start.clone().lerp(destination, i / 11);
      point.y += 1.5;
      this.spawnEffect(point, new THREE.Vector3(0, 1, 0), 'magic', 0.65 + i * 0.025);
    }
    this.player.position.copy(destination);
    this.player.blinkCooldown = cooldown;
    this.player.invulnerability = 0.45;
    this.cameraShake = Math.max(this.cameraShake, 0.45);
    this.hud.blinkFlash.classList.remove('active');
    void this.hud.blinkFlash.offsetWidth;
    this.hud.blinkFlash.classList.add('active');
    void this.audio.unlock().then(() => this.audio.blink());
  }

  private updateEncounters(): void {
    if (!this.contract.accepted) return;
    const eventDistance = this.horizontalDistance(this.player.position, EVENT_POSITION);
    if (!this.contract.eventResolved && eventDistance < 27) {
      this.openEventChoice();
      return;
    }
    if (this.contract.eventResolved && !this.pirateVisible && this.player.position.z > 250) this.revealPirate();
    if (this.pirateVisible && this.pirate.alive && this.horizontalDistance(this.player.position, this.pirate.position) < 125) {
      this.mode = 'combat';
    }
    this.nearbyAction = 'none';
    if (this.contract.pirateDefeated && !this.contract.lootCollected && this.horizontalDistance(this.player.position, this.lootGroup.position) < 16) {
      this.nearbyAction = 'loot';
    }
    if (this.contract.returning && this.horizontalDistance(this.player.position, DOCK_POSITION) < 23) {
      this.nearbyAction = 'dock';
    }
  }

  private revealPirate(): void {
    this.pirateVisible = true;
    this.pirate.object.visible = true;
    this.pirate.position.copy(PIRATE_POSITION);
    if (this.contract.eventOutcome === 'ambush') {
      this.pirate.position.set(this.player.position.x - 55, 0, this.player.position.z + 42);
      this.toast('Canvas in the fog—pirates to port!', 'danger');
    } else {
      this.toast('The Red Wraith is running up her colors.', 'danger');
    }
    this.mode = 'combat';
  }

  private defeatPirate(): void {
    this.contract.pirateDefeated = true;
    this.pirateSinking = 0.01;
    this.contract.voyageGold += 75;
    this.lootGroup.position.copy(this.pirate.position);
    this.lootGroup.position.y = 1;
    this.lootGroup.visible = true;
    this.mode = 'loot';
    this.toast('The Red Wraith is breaking apart. Recover her strongbox.', 'magic');
    this.audio.destroyed();
  }

  private collectLoot(): void {
    this.contract.lootCollected = true;
    this.contract.returning = true;
    this.contract.voyageGold += 25;
    this.lootGroup.visible = false;
    this.mode = 'sailing';
    this.nearbyAction = 'none';
    this.toast('Strongbox secured. Make for Blackwake Harbor.', 'magic');
    this.audio.ui();
  }

  private sinkPlayer(): void {
    this.mode = 'sunk';
    this.audio.destroyed();
    this.projectiles.splice(0).forEach((projectile) => this.scene.remove(projectile.object));
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop"><section class="panel dark">
        <div class="panel-kicker">Voyage lost</div><h2>The Blackwake Takes Its Due</h2>
        <p class="panel-lead">The Moonglass slips beneath the swells. Harbor salvagers find you at dawn—but not the cargo.</p>
        <div class="button-row"><button class="primary-button" data-action="respawn">Return to harbor</button></div>
      </section></div>`;
    this.modalRoot.querySelector('[data-action="respawn"]')?.addEventListener('click', () => this.respawnAtHarbor());
  }

  private respawnAtHarbor(): void {
    this.contract = this.newContract();
    this.acceptedContract = null;
    this.resetPlayer();
    this.resetPirate();
    this.mode = 'docked';
    this.cameraView = 'captain';
    this.showHarborHub();
  }

  private resolveCollisions(): void {
    const dx = this.player.position.x - HARBOR_CENTER.x;
    const dz = this.player.position.z - HARBOR_CENTER.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 59 && this.player.position.z < 18) {
      const push = (59 - distance) / Math.max(distance, 0.001);
      this.player.position.x += dx * push;
      this.player.position.z += dz * push;
      this.player.speed *= -0.18;
      this.cameraShake = Math.max(this.cameraShake, 0.25);
    }
    if (this.pirateVisible && this.pirate.alive) {
      const shipDistance = this.horizontalDistance(this.player.position, this.pirate.position);
      const minDistance = SHIPS.cutter.collisionRadius + SHIPS.pirate.collisionRadius;
      if (shipDistance < minDistance) {
        const normal = this.player.position.clone().sub(this.pirate.position).setY(0).normalize();
        const overlap = minDistance - shipDistance;
        this.player.position.addScaledVector(normal, overlap * 0.55);
        this.pirate.position.addScaledVector(normal, -overlap * 0.45);
        if (Math.abs(this.player.speed - this.pirate.speed) > 7) {
          this.damageShip(this.player, 4);
          this.damageShip(this.pirate, 5);
        }
      }
    }
    const worldRadius = Math.hypot(this.player.position.x, this.player.position.z);
    if (worldRadius > 800) {
      this.player.heading += Math.PI;
      this.player.speed *= 0.3;
      this.toast('Beyond the chart, the sea refuses passage.', 'danger');
    }
  }

  private spawnWake(dt: number, ship: ShipState): void {
    if (ship.speed < 7) return;
    this.wakeTimer -= dt;
    if (this.wakeTimer > 0 && ship.kind === 'cutter') return;
    if (ship.kind === 'cutter') this.wakeTimer = 0.19;
    if (ship.kind === 'pirate' && this.random() > 0.08) return;
    const forward = this.forward(ship.heading);
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    for (const side of [-1, 1]) {
      const bowPosition = ship.position.clone()
        .addScaledVector(forward, SHIPS[ship.kind].collisionRadius * 1.08)
        .addScaledVector(right, side * (1.7 + this.random() * 0.8));
      // Three.js Water is visually animated through normals but its depth plane
      // stays at y=0, so the foam must sit just above that plane to remain legible.
      bowPosition.y = 0.28;
      const velocity = right.clone().multiplyScalar(side * (2.8 + this.random() * 2.6)).add(new THREE.Vector3(0, 3.6 + this.random() * 3.2, 0));
      this.spawnEffect(bowPosition, velocity, 'spray', 0.62 + this.random() * 0.2);

    }
  }

  private createWakeRig(ship: ShipState): void {
    const strips = [
      [[-2.2, 8], [-4.1, 4.2], [-15.5, -42], [-10.2, -42]],
      [[2.2, 8], [4.1, 4.2], [15.5, -42], [10.2, -42]],
      [[-2.1, -4.5], [2.1, -4.5], [5.8, -48], [-5.8, -48]],
      [[-5.2, 8.5], [5.2, 8.5], [6.8, 2.1], [-6.8, 2.1]],
    ];
    const surfaces = strips.map((points, index) => this.createWakeSurface(points, index));
    surfaces.forEach(({ mesh }) => this.scene.add(mesh));
    this.wakeRigs.set(ship.kind, { ship, surfaces, lastHeading: ship.heading });
  }

  private createWakeSurface(points: number[][], phase: number): WakeSurface {
    const geometry = new THREE.BufferGeometry();
    const localXZ = new Float32Array(points.flat());
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      uniforms: {
        time: { value: 0 },
        strength: { value: 0 },
        phase: { value: phase * 1.73 },
        foamColor: { value: new THREE.Color(indexToWakeColor(phase)) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        uniform float time;
        uniform float strength;
        uniform float phase;
        uniform vec3 foamColor;
        void main() {
          float edge = smoothstep(0.0, 0.18, vUv.x) * smoothstep(0.0, 0.18, 1.0 - vUv.x);
          float trailFade = pow(max(0.0, 1.0 - vUv.y), 0.62);
          float bands = sin(vUv.y * 92.0 - time * 7.5 + sin(vUv.x * 19.0 + phase) * 2.3);
          float flecks = sin(vUv.y * 211.0 + vUv.x * 37.0 + phase * 4.0 - time * 3.0);
          float breakup = smoothstep(-0.18, 0.78, bands * 0.72 + flecks * 0.28);
          float alpha = min(0.76, strength * edge * trailFade * (0.38 + breakup * 0.72));
          gl_FragColor = vec4(foamColor, alpha);
        }
      `,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.renderOrder = 3;
    // The side sheets form the readable V. A center sheet overlaps their inner
    // edges and becomes a bright ruler-straight seam in reflective water, so it
    // stays disabled; broken bow spray supplies the turbulent center instead.
    const strengthScale = phase === 2 ? 0 : phase === 3 ? 0.45 : 0.62;
    return { mesh, localXZ, strengthScale };
  }

  private updateWakeRig(ship: ShipState, dt: number): void {
    const rig = this.wakeRigs.get(ship.kind);
    if (!rig) return;
    const maxSpeed = SHIPS[ship.kind].maxSpeed;
    const speedRatio = THREE.MathUtils.clamp(ship.speed / Math.max(maxSpeed, 1), 0, 1.25);
    const turnRate = Math.abs(this.angleDelta(rig.lastHeading, ship.heading)) / Math.max(dt, 0.001);
    rig.lastHeading = ship.heading;
    const strength = THREE.MathUtils.clamp((speedRatio - 0.03) * 1.15 + turnRate * 0.18, 0, 1);
    const sin = Math.sin(ship.heading);
    const cos = Math.cos(ship.heading);
    for (const surface of rig.surfaces) {
      surface.mesh.visible = strength > 0.025 && ship.alive && ship.object.visible;
      const positions = surface.mesh.geometry.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < 4; i += 1) {
        const localX = surface.localXZ[i * 2];
        const localZ = surface.localXZ[i * 2 + 1];
        const worldX = ship.position.x + localX * cos + localZ * sin;
        const worldZ = ship.position.z - localX * sin + localZ * cos;
        positions.setXYZ(i, worldX, 0.18, worldZ);
      }
      positions.needsUpdate = true;
      surface.mesh.geometry.computeBoundingSphere();
      surface.mesh.material.uniforms.time.value = this.elapsed;
      surface.mesh.material.uniforms.strength.value = strength * surface.strengthScale;
    }
  }

  private spawnEffect(position: THREE.Vector3, velocity: THREE.Vector3, kind: EffectState['kind'], life: number): void {
    let geometry: THREE.BufferGeometry;
    let material: THREE.MeshBasicMaterial;
    if (kind === 'wake') {
      geometry = new THREE.RingGeometry(0.8, 1.1, 18);
      material = new THREE.MeshBasicMaterial({ color: 0xd3eee8, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false });
    } else if (kind === 'magic') {
      geometry = new THREE.SphereGeometry(0.45, 8, 6);
      material = new THREE.MeshBasicMaterial({ color: 0x55ffd5, transparent: true, opacity: 0.72, depthWrite: false, blending: THREE.AdditiveBlending });
    } else if (kind === 'spray') {
      geometry = new THREE.TetrahedronGeometry(0.3 + this.random() * 0.2, 0);
      material = new THREE.MeshBasicMaterial({ color: 0xe4f6ef, transparent: true, opacity: 0.78, depthWrite: false, blending: THREE.AdditiveBlending });
    } else {
      geometry = new THREE.SphereGeometry(kind === 'splash' ? 0.7 : 0.8, 8, 6);
      material = new THREE.MeshBasicMaterial({
        color: kind === 'splash' ? 0xb9dfd5 : 0x263238,
        transparent: true,
        opacity: kind === 'splash' ? 0.5 : 0.2,
        depthWrite: false,
      });
    }
    const object = new THREE.Mesh(geometry, material);
    object.position.copy(position);
    if (kind === 'wake') object.rotation.x = -Math.PI / 2;
    this.scene.add(object);
    this.effects.push({ object, velocity, life, maxLife: life, kind });
  }

  private updateEffects(dt: number): void {
    for (let i = this.effects.length - 1; i >= 0; i -= 1) {
      const effect = this.effects[i];
      effect.life -= dt;
      effect.object.position.addScaledVector(effect.velocity, dt);
      const progress = 1 - effect.life / effect.maxLife;
      const scale = effect.kind === 'wake' ? 1 + progress * 4.5 : effect.kind === 'spray' ? 1 + progress * 0.8 : 1 + progress * 2.1;
      effect.object.scale.setScalar(scale);
      const material = effect.object.material as THREE.MeshBasicMaterial;
      const peakOpacity = effect.kind === 'magic' ? 0.72 : effect.kind === 'spray' ? 0.78 : effect.kind === 'splash' ? 0.5 : effect.kind === 'wake' ? 0.34 : 0.2;
      material.opacity = Math.max(0, (1 - progress) * peakOpacity);
      if (effect.life <= 0) {
        this.scene.remove(effect.object);
        effect.object.geometry.dispose();
        material.dispose();
        this.effects.splice(i, 1);
      }
    }
    for (let i = this.floatingTexts.length - 1; i >= 0; i -= 1) {
      const text = this.floatingTexts[i];
      text.life -= dt;
      text.world.y += dt * 4;
      if (text.life <= 0) {
        text.element.remove();
        this.floatingTexts.splice(i, 1);
      }
    }
  }

  private spawnFloatingText(world: THREE.Vector3, text: string): void {
    const element = document.createElement('div');
    element.className = 'floating-text';
    element.textContent = text;
    document.body.appendChild(element);
    this.floatingTexts.push({ element, world, life: 1.15 });
  }

  private animateMarkers(_dt: number): void {
    if (this.eventGroup.visible) {
      for (const child of this.eventGroup.children) {
        if (child === this.eventRing) continue;
        child.position.y = -1.8 + Math.sin(this.elapsed * 2 + child.userData.phase) * 0.8;
        child.scale.setScalar(0.8 + Math.sin(this.elapsed * 2.8 + child.userData.phase) * 0.15);
      }
      this.eventRing.rotation.z += 0.002;
    }
    if (this.lootGroup.visible) {
      this.lootGroup.position.y = this.sampleWave(this.lootGroup.position.x, this.lootGroup.position.z, this.elapsed) + 1.3;
      this.lootRing.rotation.z += 0.013;
    }
    this.objectiveBeacon.rotation.y += 0.0025;
  }

  private updateCamera(dt: number): void {
    if (!this.player) return;
    if (this.mode === 'title') {
      const phase = this.elapsed * 0.11;
      const target = this.player.position.clone().add(new THREE.Vector3(0, 8.5, 0));
      const angle = this.player.heading + 1.42 + this.cameraYaw + Math.sin(phase) * 0.34;
      const titleDistance = THREE.MathUtils.clamp(this.cameraDistance, 38, 60);
      const desired = new THREE.Vector3(
        target.x + Math.sin(angle) * titleDistance,
        target.y + 2.6 + Math.sin(this.cameraPitch) * 12 + Math.sin(phase * 0.7) * 0.7,
        target.z + Math.cos(angle) * titleDistance,
      );
      this.camera.position.lerp(desired, 1 - Math.exp(-dt * 1.65));
      this.camera.fov = THREE.MathUtils.damp(this.camera.fov, 43, 3.5, dt);
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(target);
      return;
    }
    if (this.mode === 'docked') {
      const target = this.harborGroup.localToWorld(new THREE.Vector3(-2, 12, -91));
      const phase = this.elapsed * 0.045;
      const desired = new THREE.Vector3(132 + Math.sin(phase) * 7, 67 + Math.sin(phase * 0.7) * 2, 112 + Math.cos(phase) * 5);
      this.camera.position.lerp(desired, 1 - Math.exp(-dt * 1.6));
      this.camera.up.lerp(UP, 1 - Math.exp(-dt * 5)).normalize();
      this.camera.fov = THREE.MathUtils.damp(this.camera.fov, 48, 3.2, dt);
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(target);
      return;
    }
    if (this.cameraView === 'deck') {
      const desired = this.player.object.localToWorld(this.deckPosition.clone());
      const shipRotation = this.player.object.getWorldQuaternion(new THREE.Quaternion());
      const cosPitch = Math.cos(this.deckPitch);
      const direction = new THREE.Vector3(
        Math.sin(this.deckYaw) * cosPitch,
        Math.sin(this.deckPitch),
        Math.cos(this.deckYaw) * cosPitch,
      ).applyQuaternion(shipRotation).normalize();
      const shipUp = UP.clone().applyQuaternion(shipRotation).normalize();
      this.camera.position.lerp(desired, 1 - Math.exp(-dt * 14));
      this.camera.up.lerp(shipUp, 1 - Math.exp(-dt * 7)).normalize();
      this.camera.fov = THREE.MathUtils.damp(this.camera.fov, 64, 5, dt);
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(this.camera.position.clone().addScaledVector(direction, 40));
      return;
    }
    this.camera.up.lerp(UP, 1 - Math.exp(-dt * 7)).normalize();
    if (this.mode === 'combat' && this.battleCameraEnabled && this.pirateVisible && this.pirate.alive) {
      const midpoint = this.player.position.clone().lerp(this.pirate.position, 0.5).add(new THREE.Vector3(0, 9, 0));
      const between = this.pirate.position.clone().sub(this.player.position).setY(0);
      const separation = Math.max(1, between.length());
      between.normalize();
      const broadside = new THREE.Vector3(-between.z, 0, between.x);
      const cameraSide = this.camera.position.clone().sub(midpoint).dot(broadside);
      if (cameraSide < 0) broadside.negate();
      const frameDistance = THREE.MathUtils.clamp(48 + separation * 0.52, 56, 96);
      const desired = midpoint.clone()
        .addScaledVector(broadside, frameDistance)
        .add(new THREE.Vector3(0, 9 + Math.min(separation * 0.06, 4), 0));
      this.camera.position.lerp(desired, 1 - Math.exp(-dt * 2.8));
      this.camera.fov = THREE.MathUtils.damp(this.camera.fov, THREE.MathUtils.clamp(48 + separation * 0.05, 49, 55), 3, dt);
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(midpoint);
      return;
    }
    const target = this.player.position.clone().add(new THREE.Vector3(0, 8, 0)).addScaledVector(this.forward(this.player.heading), 9.5);
    const orbitAngle = this.player.heading + Math.PI + this.cameraYaw;
    const horizontal = this.cameraDistance * Math.cos(this.cameraPitch);
    const desired = new THREE.Vector3(
      target.x + Math.sin(orbitAngle) * horizontal,
      target.y + Math.sin(this.cameraPitch) * this.cameraDistance,
      target.z + Math.cos(orbitAngle) * horizontal,
    );
    if (this.cameraShake > 0 && this.settings.cameraShake) {
      desired.x += (this.random() - 0.5) * this.cameraShake * 2;
      desired.y += (this.random() - 0.5) * this.cameraShake;
      this.cameraShake = Math.max(0, this.cameraShake - dt * 1.9);
    }
    this.camera.position.lerp(desired, 1 - Math.exp(-dt * 5.5));
    this.camera.fov = THREE.MathUtils.damp(this.camera.fov, 48 + Math.min(this.player.speed, 24) * 0.22, 3, dt);
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(target);
  }

  private snapCamera(): void {
    if (!this.player) return;
    if (this.mode === 'title') {
      const target = this.player.position.clone().add(new THREE.Vector3(0, 8.5, 0));
      const angle = this.player.heading + 1.42 + this.cameraYaw;
      const titleDistance = THREE.MathUtils.clamp(this.cameraDistance, 38, 60);
      this.camera.position.set(
        target.x + Math.sin(angle) * titleDistance,
        target.y + 2.6 + Math.sin(this.cameraPitch) * 12,
        target.z + Math.cos(angle) * titleDistance,
      );
      this.camera.fov = 43;
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(target);
      return;
    }
    if (this.mode === 'docked') {
      const target = this.harborGroup.localToWorld(new THREE.Vector3(-2, 12, -91));
      this.camera.position.set(132, 67, 117);
      this.camera.up.copy(UP);
      this.camera.fov = 48;
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(target);
      return;
    }
    if (this.cameraView === 'deck') {
      const desired = this.player.object.localToWorld(this.deckPosition.clone());
      const shipRotation = this.player.object.getWorldQuaternion(new THREE.Quaternion());
      const cosPitch = Math.cos(this.deckPitch);
      const direction = new THREE.Vector3(Math.sin(this.deckYaw) * cosPitch, Math.sin(this.deckPitch), Math.cos(this.deckYaw) * cosPitch)
        .applyQuaternion(shipRotation).normalize();
      this.camera.position.copy(desired);
      this.camera.up.copy(UP).applyQuaternion(shipRotation).normalize();
      this.camera.fov = 64;
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(desired.clone().addScaledVector(direction, 40));
      return;
    }
    if (this.mode === 'combat' && this.battleCameraEnabled && this.pirateVisible && this.pirate.alive) {
      const midpoint = this.player.position.clone().lerp(this.pirate.position, 0.5).add(new THREE.Vector3(0, 9, 0));
      const between = this.pirate.position.clone().sub(this.player.position).setY(0);
      const separation = Math.max(1, between.length());
      between.normalize();
      const broadside = new THREE.Vector3(-between.z, 0, between.x);
      const frameDistance = THREE.MathUtils.clamp(48 + separation * 0.52, 56, 96);
      this.camera.position.copy(midpoint)
        .addScaledVector(broadside, frameDistance)
        .add(new THREE.Vector3(0, 9 + Math.min(separation * 0.06, 4), 0));
      this.camera.fov = THREE.MathUtils.clamp(48 + separation * 0.05, 49, 55);
      this.camera.updateProjectionMatrix();
      this.camera.lookAt(midpoint);
      return;
    }
    const target = this.player.position.clone().add(new THREE.Vector3(0, 8, 0)).addScaledVector(this.forward(this.player.heading), 9.5);
    const orbitAngle = this.player.heading + Math.PI + this.cameraYaw;
    const horizontal = this.cameraDistance * Math.cos(this.cameraPitch);
    this.camera.position.set(
      target.x + Math.sin(orbitAngle) * horizontal,
      target.y + Math.sin(this.cameraPitch) * this.cameraDistance,
      target.z + Math.cos(orbitAngle) * horizontal,
    );
    this.camera.lookAt(target);
  }

  private toggleDeckView(): void {
    if (!this.player || !['sailing', 'combat', 'loot'].includes(this.mode)) {
      this.toast('Deck walk is available once the Moonglass is under way.');
      return;
    }
    this.cameraView = this.cameraView === 'deck' ? 'captain' : 'deck';
    this.draggingCamera = false;
    if (this.cameraView === 'deck') {
      this.deckPosition.set(-2.2, 6.35, -6);
      this.deckYaw = 0;
      this.deckPitch = -0.03;
      this.toast('On deck · WASD walk · drag to look · V returns to captain view.', 'magic');
    } else {
      this.toast('Captain camera restored.');
    }
    this.snapCamera();
    this.updateHud();
  }

  private updateObjective(): void {
    if (!this.contract.accepted) {
      this.objectiveBeacon.visible = false;
      return;
    }
    if (this.mode === 'combat' && this.pirateVisible) {
      this.objectiveBeacon.visible = false;
      return;
    }
    let target = EVENT_POSITION;
    if (this.contract.eventResolved && !this.contract.pirateDefeated) target = this.pirateVisible ? this.pirate.position : PIRATE_POSITION;
    if (this.contract.pirateDefeated && !this.contract.lootCollected) target = this.lootGroup.position;
    if (this.contract.returning) target = DOCK_POSITION;
    this.objectiveBeacon.visible = true;
    this.objectiveBeacon.position.set(target.x, this.sampleWave(target.x, target.z, this.elapsed), target.z);
  }

  private render(): void {
    if (!this.player) return;
    this.ocean.position.x = Math.round(this.player.position.x / 100) * 100;
    this.ocean.position.z = Math.round(this.player.position.z / 100) * 100;
    for (const floating of this.floatingTexts) {
      const projected = floating.world.clone().project(this.camera);
      floating.element.style.left = `${(projected.x * 0.5 + 0.5) * window.innerWidth}px`;
      floating.element.style.top = `${(-projected.y * 0.5 + 0.5) * window.innerHeight}px`;
      floating.element.style.opacity = `${Math.min(1, floating.life * 2)}`;
    }
    this.renderer.render(this.scene, this.camera);
  }

  private updateHud(): void {
    if (!this.player || !this.hud) return;
    this.hud.root.classList.toggle('hidden', this.mode === 'title' || this.mode === 'loading' || this.mode === 'docked');
    const maxHull = this.maxPlayerHull();
    const maxSails = this.maxPlayerSails();
    this.hud.hull.style.transform = `scaleX(${this.player.hull / maxHull})`;
    this.hud.hullText.textContent = `${Math.ceil(this.player.hull)} / ${maxHull}`;
    this.hud.sails.style.transform = `scaleX(${this.player.sails / maxSails})`;
    this.hud.sailsText.textContent = `${Math.ceil(this.player.sails)} / ${maxSails}`;
    const reload = SHIPS.cutter.reload * (this.upgrades.has('quickfuse') ? 0.85 : 1) * (this.crew === 'mara-vane' ? 0.82 : 1);
    this.hud.port.style.transform = `scaleX(${1 - this.player.portCooldown / reload})`;
    this.hud.starboard.style.transform = `scaleX(${1 - this.player.starboardCooldown / reload})`;
    const blinkMax = this.playerBlinkCooldown();
    this.hud.blink.style.transform = `scaleX(${1 - this.player.blinkCooldown / blinkMax})`;
    this.hud.cargo.textContent = this.contract.lootCollected ? '1' : '0';
    this.hud.gold.textContent = String(this.gold);
    this.hud.trim.textContent = `${Math.round(this.player.trim * 100)}%`;
    this.hud.speed.textContent = this.player.speed.toFixed(1);
    this.hud.windArrow.style.transform = `rotate(${Math.atan2(this.wind.x, this.wind.y) + Math.PI}rad)`;

    const objective = this.currentObjective();
    this.hud.objective.textContent = objective.text;
    this.hud.distance.textContent = objective.distance;
    this.hud.context.classList.toggle('hidden', this.nearbyAction === 'none');
    this.hud.contextText.textContent = this.nearbyAction === 'dock' ? 'Dock at Blackwake Harbor' : 'Recover the pirate strongbox';
    const voyageMode = ['sailing', 'combat', 'loot'].includes(this.mode);
    this.hud.cameraMode.classList.toggle('hidden', !voyageMode);
    this.hud.cameraMode.firstElementChild!.textContent = this.cameraView === 'deck' ? 'V' : (this.mode === 'combat' ? 'C' : 'V');
    this.hud.cameraMode.lastElementChild!.textContent = this.cameraView === 'deck'
      ? 'Deck walk · WASD move · drag look · V return'
      : this.mode === 'combat' && this.battleCameraEnabled
        ? 'Battle camera · C for captain · V for deck'
        : 'Captain camera · V to walk the deck';
    this.hud.deckReticle.classList.toggle('hidden', this.cameraView !== 'deck' || !voyageMode);
    const pirateReady = Boolean(this.pirate);
    this.hud.enemy.classList.toggle('hidden', !pirateReady || !this.pirateVisible || !this.pirate.alive || this.mode === 'title');
    if (pirateReady) this.hud.enemyHull.style.transform = `scaleX(${Math.max(0, this.pirate.hull / SHIPS.pirate.maxHull)})`;
  }

  private currentObjective(): { text: string; distance: string } {
    if (!this.contract.accepted) return { text: 'Choose a contract at the harbor', distance: 'Moored' };
    if (!this.contract.eventResolved) return this.objectiveLabel('Sail toward the lights off Lantern Shoal', EVENT_POSITION);
    if (!this.contract.pirateDefeated) return this.objectiveLabel('Intercept the Red Wraith', this.pirateVisible ? this.pirate.position : PIRATE_POSITION);
    if (!this.contract.lootCollected) return this.objectiveLabel('Recover the pirate strongbox', this.lootGroup.position);
    return this.objectiveLabel('Return to Blackwake Harbor', DOCK_POSITION);
  }

  private objectiveLabel(text: string, target: THREE.Vector3): { text: string; distance: string } {
    const meters = Math.round(this.horizontalDistance(this.player.position, target));
    return { text, distance: `${meters} m` };
  }

  private showTitle(): void {
    this.mode = 'title';
    this.snapCamera();
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop title-screen">
        <div class="title-brand">
          <div class="title-kicker">Open-sea fantasy</div>
          <h1 class="game-title">BLACKWAKE <span>ISLES</span></h1>
          <p class="title-copy">The map ends at the harbor mouth.</p>
        </div>
        <nav class="main-menu" aria-label="Main menu">
          <button class="menu-entry" id="start-btn"><span>01</span><strong>New voyage</strong><small>Take a contract from Blackwake Harbor</small></button>
          <button class="menu-entry" id="settings-btn"><span>02</span><strong>Sound &amp; settings</strong><small>Audio, effects, and camera shake</small></button>
        </nav>
        <div class="ship-caption">
          <small>Elven cutter · player vessel</small>
          <strong>THE MOONGLASS</strong>
          <span>Light hull / enchanted rig / blink drive</span>
        </div>
        <div class="title-footer">
          <span>Drag to orbit · Wheel to frame</span>
          <span>WASD sail · Q/E fire · V walk deck · C battle camera · Space blink</span>
        </div>
      </div>`;
    document.querySelector('#start-btn')?.addEventListener('click', () => {
      void this.audio.unlock();
      this.audio.ui();
      this.mode = 'docked';
      this.cameraView = 'captain';
      this.snapCamera();
      if (this.allegiance) this.showHarborHub();
      else this.showAllegianceChoice();
    });
    document.querySelector('#settings-btn')?.addEventListener('click', () => {
      void this.audio.unlock();
      this.audio.ui();
      this.showSettings(() => this.showTitle());
    });
  }

  private showAllegianceChoice(): void {
    this.mode = 'docked';
    this.cameraView = 'captain';
    this.snapCamera();
    const bannerArt: Record<AllegianceId, string> = {
      'free-captains': '/assets/textures/banner-free-captains.png',
      'verdant-court': '/assets/textures/banner-verdant-court.png',
      'iron-compact': '/assets/textures/banner-iron-compact.png',
    };
    const banners = ALLEGIANCES.map((faction) => `
      <button class="allegiance-banner ${this.allegiance === faction.id ? 'selected' : ''}" data-allegiance="${faction.id}" style="--faction:${faction.color};--banner-art:url('${bannerArt[faction.id]}')">
        <span class="banner-crown"></span><span class="banner-sigil">${faction.sigil}</span>
        <strong>${faction.name}</strong><em>${faction.motto}</em><small>${faction.description}</small>
        <span class="banner-benefit">${faction.benefit}</span>
      </button>`).join('');
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop allegiance-screen">
        <section class="allegiance-hall">
          <div class="panel-kicker">The Blackwake Registry · Identity layer</div>
          <h2>Whose colors will you carry?</h2>
          <p>Your allegiance changes how the Moonglass sails and how the harbor reads your name.</p>
          <div class="allegiance-banners">${banners}</div>
          ${this.allegiance ? '<button class="secondary-button allegiance-back" data-action="back">Return to harbor</button>' : ''}
        </section>
      </div>`;
    this.modalRoot.querySelectorAll<HTMLElement>('[data-allegiance]').forEach((banner) => {
      banner.addEventListener('click', () => this.chooseAllegiance(banner.dataset.allegiance as AllegianceId));
    });
    this.modalRoot.querySelector('[data-action="back"]')?.addEventListener('click', () => this.showHarborHub());
  }

  private chooseAllegiance(id: AllegianceId): void {
    this.allegiance = id;
    this.resetPlayer();
    const faction = ALLEGIANCES.find((item) => item.id === id)!;
    this.toast(`${faction.name}: ${faction.benefit}`, 'magic');
    this.audio.success();
    if (this.crew) this.showHarborHub();
    else this.showCrewQuarters();
  }

  private showCrewQuarters(): void {
    this.mode = 'docked';
    const cards = CREW.map((member) => `
      <button class="crew-card ${this.crew === member.id ? 'selected' : ''}" data-crew="${member.id}">
        <span class="crew-portrait"><i>${member.sigil}</i></span>
        <span class="crew-copy"><small>${member.role}</small><strong>${member.name}</strong><em>${member.epithet}</em><p>${member.description}</p></span>
        <span class="crew-ability"><small>${member.abilityName}</small><strong>${member.benefit}</strong></span>
      </button>`).join('');
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop tavern-screen"><section class="crew-roster">
        <header><div><div class="panel-kicker">The Keel &amp; Candle · Crew wanted</div><h2>Choose who stands beside you</h2></div><button class="secondary-button" data-action="back">Harbor</button></header>
        <p class="roster-lead">One officer joins this voyage. Their specialty changes the Moonglass immediately.</p>
        <div class="crew-grid">${cards}</div>
      </section></div>`;
    this.modalRoot.querySelectorAll<HTMLElement>('[data-crew]').forEach((card) => {
      card.addEventListener('click', () => this.chooseCrew(card.dataset.crew as CrewId));
    });
    this.modalRoot.querySelector('[data-action="back"]')?.addEventListener('click', () => this.allegiance ? this.showHarborHub() : this.showAllegianceChoice());
  }

  private chooseCrew(id: CrewId): void {
    this.crew = id;
    this.resetPlayer();
    const member = CREW.find((item) => item.id === id)!;
    this.toast(`${member.name} aboard · ${member.abilityName}`, 'magic');
    this.audio.success();
    this.showHarborHub();
  }

  private showHarborHub(): void {
    this.mode = 'docked';
    this.cameraView = 'captain';
    this.snapCamera();
    this.updateHud();
    const faction = ALLEGIANCES.find((item) => item.id === this.allegiance);
    const member = CREW.find((item) => item.id === this.crew);
    const offer = CONTRACT_OFFERS.find((item) => item.id === (this.acceptedContract ?? this.selectedContract))!;
    const ready = Boolean(this.allegiance && this.crew && this.contract.accepted);
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop harbor-hub">
        <section class="port-status">
          <span class="port-sigil">${faction?.sigil ?? '⚓'}</span><div><small>Home port</small><strong>Blackwake Harbor</strong><em>${faction?.shortName ?? 'Unsworn'} · ${this.gold} gold</em></div>
        </section>
        <button class="harbor-location keep-location" data-action="allegiance"><span>${faction?.sigil ?? '◆'}</span><strong>High Keep</strong><small>${faction ? faction.shortName : 'Pledge allegiance'}</small></button>
        <button class="harbor-location tavern-location" data-action="crew"><span>♜</span><strong>Keel &amp; Candle</strong><small>${member ? `${member.name} · ${member.role}` : 'Recruit an officer'}</small></button>
        <button class="harbor-location office-location" data-action="contracts"><span>▤</span><strong>Harbor Office</strong><small>${this.contract.accepted ? offer.title : 'Four contracts offered'}</small></button>
        <button class="harbor-location shipyard-location" data-action="shipyard"><span>⚒</span><strong>Moonglass Yard</strong><small>${this.upgrades.size} upgrades fitted</small></button>
        <div class="hub-footer">
          <button class="hub-ship"><span>◈</span><strong>The Moonglass</strong><small>${member ? member.abilityName : 'No officer aboard'}</small></button>
          <div class="voyage-readiness"><small>Voyage manifest</small><strong>${this.contract.accepted ? offer.title : 'No contract signed'}</strong><span>${faction?.shortName ?? 'Choose allegiance'} · ${member?.name ?? 'Choose crew'}</span></div>
          <button class="sail-out" data-action="sail" ${ready ? '' : 'disabled'}><span>✥</span><strong>Sail Out</strong><small>${ready ? 'Cast off for Lantern Shoal' : 'Complete the manifest'}</small></button>
        </div>
      </div>`;
    this.modalRoot.querySelector('[data-action="allegiance"]')?.addEventListener('click', () => this.showAllegianceChoice());
    this.modalRoot.querySelector('[data-action="crew"]')?.addEventListener('click', () => this.showCrewQuarters());
    this.modalRoot.querySelector('[data-action="contracts"]')?.addEventListener('click', () => this.showContractBoard());
    this.modalRoot.querySelector('[data-action="shipyard"]')?.addEventListener('click', () => this.showUpgrades(true));
    this.modalRoot.querySelector('[data-action="sail"]')?.addEventListener('click', () => this.departHarbor());
  }

  private showContractBoard(): void {
    this.mode = 'docked';
    const selected = CONTRACT_OFFERS.find((item) => item.id === this.selectedContract)!;
    const offers = CONTRACT_OFFERS.map((offer, index) => `
      <button class="contract-image-hit contract-card-hit contract-card-${index + 1} ${offer.id === this.selectedContract ? 'selected' : ''}" data-contract="${offer.id}" aria-label="Select ${offer.title}">
        <span>${offer.title}</span>
      </button>`).join('');
    const signed = this.contract.accepted && this.acceptedContract === selected.id;
    const canAccept = Boolean(this.allegiance && this.crew);
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop contract-office">
        <section class="contract-image-stage" aria-label="Blackwake Harbor contract office">
          ${offers}
          <button class="contract-image-hit contract-accept-hit ${signed ? 'signed' : ''}" data-action="accept" ${canAccept ? '' : 'disabled'} aria-label="${signed ? 'Contract signed' : `Accept ${selected.title}`}"><span>${signed ? 'Contract signed' : `Accept ${selected.title}`}</span></button>
          <button class="contract-image-hit contract-reputation-hit" data-action="reputation" aria-label="View allegiance and reputation"><span>View reputation</span></button>
          <button class="contract-image-hit contract-back-hit" data-action="back" aria-label="Return to harbor"><span>Back to harbor</span></button>
        </section>
      </div>`;
    this.modalRoot.querySelectorAll<HTMLElement>('[data-contract]').forEach((paper) => {
      paper.addEventListener('click', () => {
        this.selectedContract = paper.dataset.contract as ContractOfferId;
        this.showContractBoard();
      });
    });
    this.modalRoot.querySelector('[data-action="accept"]')?.addEventListener('click', () => this.acceptContract(this.selectedContract));
    this.modalRoot.querySelector('[data-action="reputation"]')?.addEventListener('click', () => this.showAllegianceChoice());
    this.modalRoot.querySelector('[data-action="back"]')?.addEventListener('click', () => this.showHarborHub());
  }

  private acceptContract(id: ContractOfferId): void {
    this.selectedContract = id;
    this.acceptedContract = id;
    this.contract = this.newContract();
    this.contract.accepted = true;
    this.eventGroup.visible = true;
    const offer = CONTRACT_OFFERS.find((item) => item.id === id)!;
    this.toast(`${offer.title} signed. Complete the manifest and sail.`);
    this.audio.ui();
    this.showHarborHub();
  }

  private departHarbor(): void {
    if (!this.contract.accepted || !this.allegiance || !this.crew) return;
    this.mode = 'sailing';
    this.cameraView = 'captain';
    this.cameraYaw = 0.34;
    this.modalRoot.innerHTML = '';
    this.player.trim = Math.max(this.player.trim, 0.4);
    this.updateHud();
    this.toast('Lines cast off. Lantern Shoal lies north beyond the harbor lights.', 'magic');
    this.audio.success();
  }

  private openEventChoice(): void {
    this.mode = 'event';
    this.player.speed *= 0.84;
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop"><section class="panel dark event-panel">
        <div class="event-glyph">◉</div><div class="panel-kicker">Something beneath the keel</div>
        <h2>${VERDANT_LIGHTS.title}</h2><p class="panel-lead">${VERDANT_LIGHTS.body}</p>
        <div class="choice-grid">
          <button class="choice-button" data-choice="investigate"><strong>${VERDANT_LIGHTS.investigateLabel}</strong><small>Risk the contract for whatever waits below.</small></button>
          <button class="choice-button" data-choice="ignore"><strong>${VERDANT_LIGHTS.ignoreLabel}</strong><small>Keep wind in the sails and trouble astern.</small></button>
        </div>
      </section></div>`;
    this.modalRoot.querySelector('[data-choice="investigate"]')?.addEventListener('click', () => this.resolveEvent(true));
    this.modalRoot.querySelector('[data-choice="ignore"]')?.addEventListener('click', () => this.resolveEvent(false));
  }

  private resolveEvent(investigate: boolean): void {
    this.contract.eventResolved = true;
    this.eventGroup.visible = false;
    this.modalRoot.innerHTML = '';
    if (!investigate) {
      this.contract.eventOutcome = 'none';
      this.toast('The lights sink into black water behind you.');
    } else if (this.forcedEventOutcome === 'relic' || (this.forcedEventOutcome !== 'ambush' && this.random() < VERDANT_LIGHTS.relicWeight)) {
      this.contract.eventOutcome = 'relic';
      this.contract.relics = 1;
      this.contract.voyageGold += 40;
      this.toast('You raise a verdant relic, cold as moonlight. Worth 40 gold.', 'magic');
      this.audio.success();
    } else {
      this.contract.eventOutcome = 'ambush';
      this.toast('The lights vanish. Canvas moves in the fog ahead.', 'danger');
    }
    this.mode = 'sailing';
  }

  private showResults(): void {
    this.mode = 'results';
    this.nearbyAction = 'none';
    const offer = CONTRACT_OFFERS.find((item) => item.id === (this.acceptedContract ?? 'wraith-hunt'))!;
    const base = offer.reward;
    const allegianceBonus = this.allegiance === 'free-captains' ? Math.round(base * 0.25) : 0;
    const total = base + this.contract.voyageGold + allegianceBonus;
    this.gold += total;
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop"><section class="panel dark">
        <div class="panel-kicker">Contract complete</div><h2>Safe Harbor, Heavy Hold</h2>
        <p class="panel-lead">The harbormaster counts the Wraith's seal among the wreckage. For tonight, at least, Lantern Shoal belongs to honest captains.</p>
        <div class="results-grid"><div><small>${offer.title}</small><strong>${base} gold</strong></div><div><small>Recovered cargo</small><strong>${this.contract.voyageGold} gold</strong></div><div><small>Allegiance bonus</small><strong>${allegianceBonus} gold</strong></div><div><small>Relics</small><strong>${this.contract.relics}</strong></div></div>
        <div class="button-row"><button class="primary-button" data-action="upgrades">Spend ${total} gold</button></div>
      </section></div>`;
    this.modalRoot.querySelector('[data-action="upgrades"]')?.addEventListener('click', () => this.showUpgrades(false));
    this.audio.success();
  }

  private showUpgrades(fromHarbor = false): void {
    this.mode = 'upgrade';
    const cards = UPGRADES.map((upgrade) => {
      const purchased = this.upgrades.has(upgrade.id);
      return `<button class="upgrade-card ${purchased ? 'purchased' : ''}" data-upgrade="${upgrade.id}" ${purchased ? 'disabled' : ''}>
        <span class="upgrade-icon">${upgrade.icon}</span><strong>${upgrade.name}</strong><small>${upgrade.description}</small><span class="upgrade-price">${purchased ? 'FITTED' : `${upgrade.price} GOLD`}</span>
      </button>`;
    }).join('');
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop"><section class="panel dark">
        <div class="panel-kicker">Blackwake chandlery · ${this.gold} gold available</div><h2>Make Her Stranger</h2>
        <p class="panel-lead">Every captain swears their ship has a soul. Gold merely teaches it new hungers.</p>
        <div class="upgrade-grid">${cards}</div>
        <div class="button-row"><button class="secondary-button" data-action="sail-again">${fromHarbor ? 'Return to harbor' : 'Return to harbor hub'}</button></div>
      </section></div>`;
    this.modalRoot.querySelectorAll<HTMLElement>('[data-upgrade]').forEach((card) => {
      card.addEventListener('click', () => this.purchaseUpgrade(card.dataset.upgrade as UpgradeDefinition['id'], fromHarbor));
    });
    this.modalRoot.querySelector('[data-action="sail-again"]')?.addEventListener('click', () => fromHarbor ? this.showHarborHub() : this.prepareNextVoyage());
  }

  private purchaseUpgrade(id: UpgradeDefinition['id'], fromHarbor = false): void {
    const upgrade = UPGRADES.find((item) => item.id === id);
    if (!upgrade || this.upgrades.has(id) || this.gold < upgrade.price) return;
    this.gold -= upgrade.price;
    this.upgrades.add(id);
    if (id === 'reinforced') {
      this.player.hull += 20;
    }
    this.toast(`${upgrade.name} fitted to the Moonglass.`, 'magic');
    this.audio.success();
    this.showUpgrades(fromHarbor);
  }

  private prepareNextVoyage(): void {
    this.contract = this.newContract();
    this.acceptedContract = null;
    this.resetPlayer();
    this.resetPirate();
    this.mode = 'docked';
    this.showHarborHub();
  }

  private showSettings(onClose: () => void): void {
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop"><section class="panel dark">
        <div class="panel-kicker">Captain's preferences</div><h2>Sound & Settings</h2>
        <div class="settings-list">
          ${this.settingSlider('Master volume', 'master', this.settings.masterVolume)}
          ${this.settingSlider('Ambience', 'music', this.settings.musicVolume)}
          ${this.settingSlider('Effects', 'effects', this.settings.effectsVolume)}
        </div>
        <div class="button-row"><button class="primary-button" data-action="close">Return</button></div>
      </section></div>`;
    this.modalRoot.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((input) => {
      input.addEventListener('input', () => {
        const value = Number(input.value);
        if (input.dataset.setting === 'master') this.settings.masterVolume = value;
        if (input.dataset.setting === 'music') this.settings.musicVolume = value;
        if (input.dataset.setting === 'effects') this.settings.effectsVolume = value;
        input.parentElement!.querySelector('output')!.textContent = `${Math.round(value * 100)}%`;
        this.audio.updateVolumes();
      });
    });
    this.modalRoot.querySelector('[data-action="close"]')?.addEventListener('click', onClose);
  }

  private settingSlider(label: string, id: string, value: number): string {
    return `<label class="setting-row"><span>${label}</span><input type="range" min="0" max="1" step="0.01" value="${value}" data-setting="${id}"><output>${Math.round(value * 100)}%</output></label>`;
  }

  private togglePause(): void {
    if (this.mode === 'title' || this.mode === 'loading' || this.mode === 'event' || this.mode === 'results' || this.mode === 'upgrade' || this.mode === 'docked') return;
    if (this.mode === 'paused') {
      this.mode = this.priorMode;
      this.modalRoot.innerHTML = '';
      return;
    }
    this.priorMode = this.mode;
    this.mode = 'paused';
    this.modalRoot.innerHTML = `
      <div class="modal-backdrop"><section class="panel dark">
        <div class="panel-kicker">Voyage paused</div><h2>The Sea Can Wait</h2>
        <p class="panel-lead">For a moment.</p>
        <div class="button-row"><button class="primary-button" data-action="resume">Resume</button><button class="secondary-button" data-action="settings">Sound & settings</button><button class="secondary-button" data-action="restart">Restart voyage</button></div>
      </section></div>`;
    this.modalRoot.querySelector('[data-action="resume"]')?.addEventListener('click', () => this.togglePause());
    this.modalRoot.querySelector('[data-action="settings"]')?.addEventListener('click', () => this.showSettings(() => { this.mode = 'paused'; this.togglePause(); }));
    this.modalRoot.querySelector('[data-action="restart"]')?.addEventListener('click', () => this.respawnAtHarbor());
  }

  private interact(): void {
    if (this.nearbyAction === 'loot') this.collectLoot();
    if (this.nearbyAction === 'dock') this.showResults();
  }

  private resetPlayer(): void {
    this.lastBroadside = null;
    this.player.position.copy(PLAYER_SPAWN);
    this.player.heading = 0;
    this.player.speed = 0;
    this.player.trim = 0.34;
    this.player.hull = this.maxPlayerHull();
    this.player.sails = this.maxPlayerSails();
    this.player.portCooldown = 0;
    this.player.starboardCooldown = 0;
    this.player.blinkCooldown = 0;
    this.player.alive = true;
    this.player.object.visible = true;
    this.wakeRigs.get('cutter')?.surfaces.forEach((surface) => { surface.mesh.visible = false; });
  }

  private resetPirate(): void {
    this.pirate.position.copy(PIRATE_POSITION);
    this.pirate.heading = Math.PI;
    this.pirate.speed = 0;
    this.pirate.hull = SHIPS.pirate.maxHull;
    this.pirate.sails = SHIPS.pirate.maxSails;
    this.pirate.alive = true;
    this.pirate.object.visible = false;
    this.pirateVisible = false;
    this.pirateSinking = 0;
    this.wakeRigs.get('pirate')?.surfaces.forEach((surface) => { surface.mesh.visible = false; });
    this.lootGroup.visible = false;
    this.eventGroup.visible = false;
    this.projectiles.splice(0).forEach((projectile) => this.scene.remove(projectile.object));
    for (const effect of this.effects.splice(0)) {
      this.scene.remove(effect.object);
      effect.object.geometry.dispose();
      const material = effect.object.material;
      if (Array.isArray(material)) material.forEach((item) => item.dispose());
      else material.dispose();
    }
    for (const floating of this.floatingTexts.splice(0)) floating.element.remove();
  }

  private installTestHooks(): void {
    window.render_game_to_text = () => JSON.stringify({
      coordinateSystem: 'right-handed; +x east, +y up, +z north; headings are radians clockwise from +z',
      mode: this.mode,
      testMode: this.testMode,
      objective: this.player ? this.currentObjective().text : 'loading',
      player: this.player ? {
        x: Number(this.player.position.x.toFixed(2)),
        y: Number(this.player.position.y.toFixed(2)),
        z: Number(this.player.position.z.toFixed(2)),
        heading: Number(this.player.heading.toFixed(3)),
        speed: Number(this.player.speed.toFixed(2)),
        trim: Number(this.player.trim.toFixed(2)),
        hull: Number(this.player.hull.toFixed(1)),
        sails: Number(this.player.sails.toFixed(1)),
        portCooldown: Number(this.player.portCooldown.toFixed(2)),
        starboardCooldown: Number(this.player.starboardCooldown.toFixed(2)),
        blinkCooldown: Number(this.player.blinkCooldown.toFixed(2)),
      } : null,
      wind: { x: Number(this.wind.x.toFixed(2)), z: Number(this.wind.y.toFixed(2)) },
      pirate: this.pirateVisible ? {
        x: Number(this.pirate.position.x.toFixed(2)), z: Number(this.pirate.position.z.toFixed(2)), hull: Number(this.pirate.hull.toFixed(1)), alive: this.pirate.alive,
      } : null,
      contract: this.contract,
      nearbyAction: this.nearbyAction,
      projectiles: this.projectiles.length,
      effects: this.effects.length,
      gold: this.gold,
      upgrades: [...this.upgrades],
      allegiance: this.allegiance,
      crew: this.crew,
      selectedContract: this.selectedContract,
      acceptedContract: this.acceptedContract,
      lastBroadside: this.lastBroadside,
      camera: {
        mode: this.cameraView === 'deck' ? 'deck' : this.mode === 'combat' && this.battleCameraEnabled ? 'battle' : this.mode === 'docked' ? 'harbor' : 'captain',
        yaw: Number(this.cameraYaw.toFixed(2)),
        pitch: Number(this.cameraPitch.toFixed(2)),
        distance: Number(this.cameraDistance.toFixed(1)),
        deckPosition: {
          x: Number(this.deckPosition.x.toFixed(2)),
          y: Number(this.deckPosition.y.toFixed(2)),
          z: Number(this.deckPosition.z.toFixed(2)),
          yaw: Number(this.deckYaw.toFixed(2)),
          pitch: Number(this.deckPitch.toFixed(2)),
        },
      },
      wake: [...this.wakeRigs.values()].map((rig) => ({
        ship: rig.ship.kind,
        visible: rig.surfaces.some((surface) => surface.mesh.visible),
        strength: Number((rig.surfaces[0]?.mesh.material.uniforms.strength.value ?? 0).toFixed(2)),
      })),
      cloth: [...this.clothRigs.values()].map((rig) => ({
        ship: rig.ship.kind,
        strength: Number(rig.uniforms.strength.value.toFixed(2)),
        flutter: Number(rig.uniforms.flutter.value.toFixed(2)),
        wind: {
          x: Number(rig.uniforms.wind.value.x.toFixed(2)),
          z: Number(rig.uniforms.wind.value.z.toFixed(2)),
        },
      })),
      audio: this.audio.diagnosticState(),
    });
    window.advanceTime = (ms: number) => {
      if (this.mode === 'loading') return;
      const steps = Math.max(1, Math.round(ms / (1000 / 60)));
      for (let i = 0; i < steps; i += 1) this.update(FIXED_STEP);
      this.render();
    };
  }

  private handleDebugKey(key: string): void {
    if (key === '1' && this.contract.accepted) {
      this.player.position.copy(EVENT_POSITION).add(new THREE.Vector3(0, 0, -12));
    }
    if (key === '2' && this.contract.accepted) {
      this.contract.eventResolved = true;
      this.eventGroup.visible = false;
      this.player.position.copy(PIRATE_POSITION).add(new THREE.Vector3(0, 0, -55));
      this.revealPirate();
    }
    if (key === '3' && this.pirateVisible) {
      this.pirate.hull = 1;
      this.damageShip(this.pirate, 2);
    }
    if (key === '4' && this.contract.pirateDefeated) {
      this.collectLoot();
      this.player.position.copy(DOCK_POSITION).add(new THREE.Vector3(0, 0, 7));
    }
  }

  private applyTestScenario(scenario: string): void {
    this.modalRoot.innerHTML = '';
    this.allegiance ??= 'verdant-court';
    this.crew ??= 'mara-vane';
    this.acceptedContract = 'wraith-hunt';
    this.contract = this.newContract();
    this.contract.accepted = true;
    this.eventGroup.visible = true;
    this.mode = 'sailing';
    this.cameraYaw = 0.34;
    if (scenario === 'allegiance') {
      this.allegiance = null;
      this.crew = null;
      this.contract = this.newContract();
      this.acceptedContract = null;
      this.showAllegianceChoice();
      return;
    }
    if (scenario === 'crew') {
      this.contract = this.newContract();
      this.acceptedContract = null;
      this.showCrewQuarters();
      return;
    }
    if (scenario === 'harbor') {
      this.contract = this.newContract();
      this.acceptedContract = null;
      this.showHarborHub();
      return;
    }
    if (scenario === 'contracts') {
      this.contract = this.newContract();
      this.acceptedContract = null;
      this.showContractBoard();
      return;
    }
    if (scenario === 'deck') {
      this.mode = 'sailing';
      this.cameraView = 'deck';
      this.player.position.set(20, 0, 150);
      this.player.speed = 13;
      this.player.trim = 0.68;
      this.snapCamera();
    }
    if (scenario === 'wake') {
      this.mode = 'sailing';
      this.player.position.set(20, 0, 150);
      this.player.speed = 19;
      this.player.trim = 0.9;
    }
    if (scenario === 'event') {
      this.player.position.copy(EVENT_POSITION).add(new THREE.Vector3(0, 0, -10));
      this.openEventChoice();
    }
    if (scenario === 'combat') {
      this.contract.eventResolved = true;
      this.contract.eventOutcome = 'relic';
      this.eventGroup.visible = false;
      this.player.position.copy(PIRATE_POSITION).add(new THREE.Vector3(-14, 0, 0));
      this.revealPirate();
    }
    if (scenario === 'loot' || scenario === 'dock') {
      this.contract.eventResolved = true;
      this.eventGroup.visible = false;
      this.pirateVisible = true;
      this.pirate.object.visible = false;
      this.pirate.alive = false;
      this.contract.pirateDefeated = true;
      this.contract.voyageGold = 75;
      this.lootGroup.position.copy(PIRATE_POSITION);
      this.lootGroup.visible = scenario === 'loot';
      this.mode = 'loot';
      this.player.position.copy(PIRATE_POSITION).add(new THREE.Vector3(0, 0, -8));
      this.nearbyAction = 'loot';
    }
    if (scenario === 'dock') {
      this.contract.lootCollected = true;
      this.contract.returning = true;
      this.contract.voyageGold = 100;
      this.lootGroup.visible = false;
      this.player.position.copy(DOCK_POSITION).add(new THREE.Vector3(0, 0, 7));
      this.mode = 'sailing';
      this.nearbyAction = 'dock';
    }
    if (scenario === 'upgrade') {
      this.gold = 175;
      this.contract.eventResolved = true;
      this.contract.pirateDefeated = true;
      this.contract.lootCollected = true;
      this.contract.returning = true;
      this.showUpgrades();
    }
    if (scenario === 'sunk') {
      this.mode = 'combat';
      this.player.hull = 1;
      this.damageShip(this.player, 2);
    }
    if (scenario === 'defeat') {
      this.contract.eventResolved = true;
      this.eventGroup.visible = false;
      this.pirateVisible = true;
      this.pirate.object.visible = true;
      this.pirate.hull = 1;
      this.pirate.alive = true;
      this.mode = 'combat';
      this.damageShip(this.pirate, 2);
    }
    if (scenario === 'paused') {
      this.mode = 'sailing';
      this.togglePause();
    }
  }

  private async toggleFullscreen(): Promise<void> {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
    else await document.exitFullscreen();
    this.resize();
  }

  private resize = (): void => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };

  private newContract(): ContractState {
    return {
      accepted: false,
      eventResolved: false,
      eventOutcome: 'none',
      pirateDefeated: false,
      lootCollected: false,
      returning: false,
      voyageGold: 0,
      relics: 0,
    };
  }

  private playerDefinition(): ShipDefinition {
    return SHIPS.cutter;
  }

  private maxPlayerHull(): number {
    return SHIPS.cutter.maxHull
      + (this.upgrades.has('reinforced') ? 20 : 0)
      + (this.crew === 'brann-oakhand' ? 20 : 0)
      + (this.allegiance === 'iron-compact' ? 10 : 0);
  }

  private maxPlayerSails(): number {
    return SHIPS.cutter.maxSails + (this.crew === 'brann-oakhand' ? 15 : 0);
  }

  private playerBlinkCooldown(): number {
    const upgradeCooldown = this.upgrades.has('runed-rigging') ? 6.8 : 8;
    return upgradeCooldown * (this.crew === 'ilyra-moss' ? 0.75 : 1);
  }

  private windFactor(heading: number): number {
    return relativeWindFactor(heading, this.wind.x, this.wind.y);
  }

  private forward(heading: number): THREE.Vector3 {
    return new THREE.Vector3(Math.sin(heading), 0, Math.cos(heading));
  }

  private horizontalDistance(a: THREE.Vector3, b: THREE.Vector3): number {
    return Math.hypot(a.x - b.x, a.z - b.z);
  }

  private angleDelta(from: number, to: number): number {
    return shortestAngleDelta(from, to);
  }

  private random(): number {
    const result = seededRandom(this.rngState);
    this.rngState = result.seed;
    return result.value;
  }

  private setLoading(percent: number, text: string, bar: HTMLElement | null, label: HTMLElement | null): void {
    if (bar) bar.style.width = `${percent}%`;
    if (label) label.textContent = text;
  }

  private toast(message: string, tone: 'normal' | 'magic' | 'danger' = 'normal'): void {
    const element = document.createElement('div');
    element.className = `toast ${tone === 'normal' ? '' : tone}`;
    element.textContent = message;
    this.toastRoot.appendChild(element);
    window.setTimeout(() => element.remove(), 3500);
  }
}
